import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { NavLink, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  createCloudMedicine,
  createPersonalHousehold,
  acceptHouseholdInvitationLink,
  createHouseholdInvitation,
  claimLegacyMedicalProfile,
  ensureMedicalProfile,
  ensureCloudContext,
  loadCloudMedicines,
  previewHouseholdInvitation,
  renameHousehold,
  requestPasswordReset,
  setNewPassword,
  signInWithPassword,
  signOut,
  signUpWithPassword,
  softDeleteCloudMedicine,
  updateCloudMedicine,
  type CloudContext,
  type FamilyMember,
} from './lib/cloudPharmacy';
import { bulkUpdateLabResultMetadataForDocument, createCloudLabResult, enrichCloudLabResultNames, isCloudLabResultDuplicateError, loadCloudLabResults, softDeleteCloudLabResult, softDeleteCloudLabResultsForDocument, softDeleteCloudLabResultsForOwner, updateCloudLabResult } from './lib/cloudLabResults';
import { ARCHIVE_CATEGORY_LABELS, DOCUMENT_PROCESSING_STATUS_LABELS, deleteLabDocument, finalizeLabDocument, formatOptionalDocumentDate, formatOptionalDocumentDateTime, getLabDocumentDataUrl, linkLabResultsToDocument, loadArchiveDocuments, loadDocumentProcessingDiagnostics, loadLabDocuments, openLabSourceDocument, publicProcessingError, shareLabSourceDocument, updateLabDocumentMetadata, uploadArchiveDocument, uploadLabDocument, type ArchiveCategory, type DocumentDeletionMode, type DocumentDeletionOutcome, type DocumentProcessingDiagnostics, type MedicalDocument } from './lib/cloudLabDocuments';
import { isSupabaseConfigured, supabase } from './lib/supabase';
import { loadData, loadLocalModePreference, makeId, resolveActiveProfile, type Country, type LabResult, type Medicine, type Owner, type Profile, saveData, saveLocalModePreference } from './lib/localData';
import { compressImage, fileToDataUrl } from './lib/image';
import { recognizeMedicinePhoto } from './lib/ocr';
import { describeMedicine, findMedicineDosing } from './lib/aiDescribe';
import { analyzeMedicinePhoto } from './lib/aiVision';
import { analyzeLabReport, loadLabDocumentReview, processLabDocument, type DocumentMetadataOptions, type DocumentProcessingProgress, type LabExtraction, type MetadataCandidate } from './lib/aiLabExtract';
import { recommendLabTrend } from './lib/aiLabRecommend';
import { loadCachedRecommendation, saveCachedRecommendation, type CachedLabRecommendation } from './lib/cloudLabRecommendations';
import { translatePanel } from './lib/panelTranslations';
import { withWakeLock } from './lib/wakeLock';
import { inRange, isNumericLabValue, labSeriesKey, seriesReadingsForKey } from './lib/labMath';
import { matchesLabDocumentSearch, matchesLabResultSearch } from './lib/labSearch';
import { formatLabNumber, parseReferenceText, rangeGeometry, referenceCondition } from './lib/labReference';
import { knownLabInstitutions } from './lib/labInstitutions';
import { DocumentHasLinkedResultsError } from './lib/documentDeletion';
import { formatIsoDate, formatTrendDelta, numericTrend, qualitativeSentence, qualitativeTrend, trendSentence, type NumericTrend, type QualitativeTrend } from './lib/labTrend';
import { duplicateLabResultIndexes, isDuplicateLabResult, similarManualLabResultIndexes } from './lib/labDeduplication';
import { matchLabResultsForEnrichment } from './lib/labEnrichment';
import { invitationToken } from './lib/invitationLink';
import { daysUntilExpiry, expiryStatus } from './lib/medicineExpiry';
import {
  ANTHROPIC_MODEL_OPTIONS,
  getAnthropicModel,
  setAnthropicModel,
} from './lib/aiKey';

const COUNTRY_LABELS: Record<Country, string> = { russia: 'Россия', israel: 'Израиль', other: 'Другое' };
type LabReportSaveOutcome = { savedCount: number; skippedDuplicateCount: number };

const localMembers: FamilyMember[] = [
  { userId: 'anton', displayName: 'Антон', role: 'owner' },
  { userId: 'anya', displayName: 'Аня', role: 'member' },
];

function profileName(profile: Profile, members: FamilyMember[]): string {
  return members.find(member => member.userId === profile)?.displayName || (profile === 'anton' ? 'Антон' : profile === 'anya' ? 'Аня' : 'Участник семьи');
}

function LabInstitutionField({ id, names, value, onChange, placeholder, required }: {
  id: string;
  names: string[];
  value: string;
  onChange: (name: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const pickerId = `${id}-saved`;
  return <div className="lab-institution-field">
    <label htmlFor={id}>Лаборатория</label>
    <span className={names.length ? 'lab-institution-combobox' : 'lab-institution-combobox is-empty'}>
      <input id={id} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} required={required} />
      <select
        id={pickerId}
        aria-label={names.length ? 'Выбрать лабораторию из сохранённых' : 'Сохранённых лабораторий пока нет'}
        title={names.length ? 'Выбрать лабораторию из сохранённых' : 'Сохранённых лабораторий пока нет'}
        value=""
        disabled={!names.length}
        onChange={event => {
          const name = event.target.value;
          if (name) onChange(name);
        }}
      >
        <option value="" disabled>{names.length ? 'Выберите лабораторию' : 'Сохранённых лабораторий пока нет'}</option>
        {names.map(name => <option key={name} value={name}>{name}</option>)}
      </select>
    </span>
  </div>;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const nav = [
  { to: '/dashboard', label: 'Обзор', icon: '⌂' },
  { to: '/metrics', label: 'Показатели', icon: '♡' },
  { to: '/documents', label: 'Документы', icon: '▤' },
  { to: '/pharmacy', label: 'Аптечка', icon: '✚' },
  { to: '/more', label: 'Ещё', icon: '•••' },
] as const;

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  // Supabase throws plain PostgrestError/AuthError-shaped objects, not real
  // Error instances — surface their message (+ hint/details when present)
  // instead of hiding the real cause behind a generic string.
  if (error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    const supabaseError = error as { message: string; details?: string; hint?: string; code?: string };
    const extra = [supabaseError.details, supabaseError.hint, supabaseError.code].filter(Boolean).join(' · ');
    return extra ? `${supabaseError.message} (${extra})` : supabaseError.message;
  }
  return 'Произошла неизвестная ошибка';
}

export default function App() {
  const [data, setData] = useState(loadData);
  const [session, setSession] = useState<Session | null>(null);
  const [cloud, setCloud] = useState<CloudContext | null>(null);
  const [starting, setStarting] = useState(isSupabaseConfigured);
  const [busy, setBusy] = useState(false);
  const [cloudError, setCloudError] = useState('');
  const [documents, setDocuments] = useState<MedicalDocument[]>([]);
  const [archiveDocuments, setArchiveDocuments] = useState<MedicalDocument[]>([]);
  const [localMode, setLocalModeState] = useState(() => !isSupabaseConfigured || loadLocalModePreference());
  const setLocalMode = (active: boolean) => {
    saveLocalModePreference(active);
    setLocalModeState(active);
  };
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [invite, setInvite] = useState<{ token: string; householdName: string } | null>(null);

  useEffect(() => saveData(data), [data]);

  useEffect(() => {
    if (!supabase) return;
    let mounted = true;
    supabase.auth.getSession().then(({ data: result }) => {
      if (mounted) setSession(result.session);
      if (mounted && !result.session) setStarting(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
      setSession(nextSession);
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) {
      setCloud(null);
      setDocuments([]);
      setArchiveDocuments([]);
      return;
    }
    let cancelled = false;
    setStarting(true);
    setCloudError('');
    ensureCloudContext(session)
      .then(async context => {
        const [medicines, labResults, cloudDocuments, cloudArchiveDocuments] = context.householdId ? await Promise.all([
          loadCloudMedicines(context.householdId),
          loadCloudLabResults(context.householdId),
          loadLabDocuments(context.householdId),
          loadArchiveDocuments(context.householdId),
        ]) : [[], [], [], []];
        if (!cancelled) {
          setCloud(context);
          setData(current => ({
            ...current,
            medicines,
            labResults,
            activeUser: resolveActiveProfile(
              current.activeUser,
              context.members.map(member => member.userId),
              context.session.user.id,
            ),
          }));
          setDocuments(cloudDocuments);
          setArchiveDocuments(cloudArchiveDocuments);
          setLocalMode(false);
        }
      })
      .catch(error => !cancelled && setCloudError(errorText(error)))
      .finally(() => !cancelled && setStarting(false));
    return () => { cancelled = true; };
  }, [session]);

  useEffect(() => {
    const token = invitationToken(window.location.search);
    if (!session || !token) return;
    void previewHouseholdInvitation(token).then(found => found && setInvite({ token, householdName: found.householdName })).catch(() => {});
  }, [session]);

  const setProfile = (activeUser: Profile) => setData(current => ({ ...current, activeUser }));

  const medicalProfileIdFor = async (owner: Profile): Promise<string | undefined> => {
    if (!cloud) return undefined;
    const member = cloud.members.find(item => item.userId === owner);
    if (!member) return undefined;
    return member.medicalProfileId || ensureMedicalProfile(member.userId);
  };

  const saveMedicine = async (medicine: Medicine) => {
    setBusy(true);
    setCloudError('');
    try {
      const saved = cloud
        ? await createCloudMedicine(cloud.householdId, cloud.session.user.id, medicine)
        : medicine;
      setData(current => ({ ...current, medicines: [saved, ...current.medicines] }));
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const updateMedicine = async (medicine: Medicine) => {
    setBusy(true);
    setCloudError('');
    try {
      if (cloud) await updateCloudMedicine(medicine);
      setData(current => ({ ...current, medicines: current.medicines.map(item => item.id === medicine.id ? medicine : item) }));
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const removeMedicine = async (id: string) => {
    setBusy(true);
    setCloudError('');
    try {
      if (cloud) await softDeleteCloudMedicine(id);
      setData(current => ({ ...current, medicines: current.medicines.filter(item => item.id !== id) }));
    } catch (error) {
      setCloudError(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const saveLabResult = async (result: LabResult) => {
    setBusy(true);
    setCloudError('');
    try {
      const saved = cloud
        ? await createCloudLabResult(cloud.householdId, cloud.session.user.id, { ...result, medicalProfileId: await medicalProfileIdFor(result.owner) })
        : result;
      setData(current => ({ ...current, labResults: [...current.labResults, saved] }));
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const saveLabReport = async (
    results: LabResult[],
    source?: { dataUrl?: string; documentId?: string; owner: Profile; country: Country; takenOn: string; labName?: string },
  ): Promise<LabReportSaveOutcome> => {
    setBusy(true);
    setCloudError('');
    try {
      const documentId = cloud ? source?.documentId : undefined;

      const saved: LabResult[] = [];
      let skippedDuplicateCount = 0;
      const knownResults = [...data.labResults];
      for (let index = 0; index < results.length; index += 1) {
        const prepared: LabResult = {
          ...results[index],
          medicalProfileId: await medicalProfileIdFor(results[index].owner),
          documentId,
          // Local mode keeps the historical compatibility behaviour without
          // copying a large data URL into every reading. Cloud mode stores the
          // source once in Storage and all readings share documentId.
          sourceDocument: cloud ? undefined : index === 0 ? source?.dataUrl : undefined,
        };
        if (knownResults.some(existing => isDuplicateLabResult(prepared, existing))) {
          skippedDuplicateCount += 1;
          continue;
        }
        try {
          const savedResult = cloud
            ? await createCloudLabResult(cloud.householdId, cloud.session.user.id, prepared)
            : prepared;
          saved.push(savedResult);
          knownResults.push(savedResult);
        } catch (error) {
          // The client check above protects the ordinary path; the database
          // constraint covers a simultaneous confirmation from another device.
          if (cloud && isCloudLabResultDuplicateError(error)) {
            skippedDuplicateCount += 1;
            continue;
          }
          throw error;
        }
      }
      setData(current => ({ ...current, labResults: [...current.labResults, ...saved] }));
      if (cloud && documentId && source) {
        await finalizeLabDocument(documentId, {
          takenOn: source.takenOn,
          labName: source.labName,
          country: source.country,
          confirmedCount: data.labResults.filter(result => result.documentId === documentId).length + saved.length,
          reviewCount: 0,
        });
        setDocuments(await loadLabDocuments(cloud.householdId));
      }
      return { savedCount: saved.length, skippedDuplicateCount };
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const uploadReportDocument = async (file: File, owner: Profile): Promise<string> => {
    if (!cloud) throw new Error('Загрузка документа доступна только в облачном режиме');
    setBusy(true);
    setCloudError('');
    try {
      const documentId = await uploadLabDocument({
        householdId: cloud.householdId,
        userId: cloud.session.user.id,
        owner,
        medicalProfileId: await medicalProfileIdFor(owner),
        file,
        fileName: file.name,
      });
      setDocuments(await loadLabDocuments(cloud.householdId));
      return documentId;
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  // Attaches a source file to one specific reading that doesn't have one yet
  // -- the contextual "Прикрепить файл" action on a reading row. Unlike the
  // old global "attach existing document" flow (removed: it required the
  // user to re-enter owner/date/lab exactly to find a match, which was
  // confusing), this always targets the one reading the user clicked from,
  // so there's nothing to match -- it reuses that reading's own metadata.
  const attachDocumentToReading = async (reading: LabResult, file: File) => {
    if (!cloud) throw new Error('Добавление документов доступно только в облачном режиме');
    setBusy(true);
    setCloudError('');
    try {
      const documentId = await uploadLabDocument({
        householdId: cloud.householdId,
        userId: cloud.session.user.id,
        owner: reading.owner,
        medicalProfileId: await medicalProfileIdFor(reading.owner),
        country: reading.country,
        takenOn: reading.takenOn,
        labName: reading.labName,
        fileName: file.name,
        file,
      });
      await linkLabResultsToDocument([reading.id], documentId);
      setData(current => ({
        ...current,
        labResults: current.labResults.map(result => result.id === reading.id ? { ...result, documentId, sourceDocument: undefined } : result),
      }));
      setDocuments(await loadLabDocuments(cloud.householdId));
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const addArchiveDocument = async (input: { file: File; owner: Profile; category: ArchiveCategory; title: string; note?: string; takenOn?: string }) => {
    if (!cloud) throw new Error('Добавление документов доступно только в облачном режиме');
    setBusy(true);
    setCloudError('');
    try {
      await uploadArchiveDocument({
        householdId: cloud.householdId,
        userId: cloud.session.user.id,
        owner: input.owner,
        medicalProfileId: await medicalProfileIdFor(input.owner),
        category: input.category,
        title: input.title,
        note: input.note,
        takenOn: input.takenOn,
        fileName: input.file.name,
        file: input.file,
      });
      setArchiveDocuments(await loadArchiveDocuments(cloud.householdId));
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const removeArchiveDocument = async (documentId: string) => {
    if (!cloud) throw new Error('Добавление документов доступно только в облачном режиме');
    setBusy(true);
    try {
      await deleteLabDocument(documentId, 'document_only');
      setArchiveDocuments(await loadArchiveDocuments(cloud.householdId));
    } finally {
      setBusy(false);
    }
  };

  const enrichDocumentLabResults = async (document: MedicalDocument): Promise<{ updated: number; skipped: number }> => {
    if (!cloud) throw new Error('Обновление показателей доступно только в облачном режиме');
    const documentResults = data.labResults.filter(result => result.documentId === document.id);
    if (!documentResults.length) throw new Error('С этим документом не связано ни одного показателя');

    setBusy(true);
    setCloudError('');
    try {
      const documentDataUrl = await getLabDocumentDataUrl(document.id);
      const extracted = await withWakeLock(() => analyzeLabReport(documentDataUrl, getAnthropicModel()));
      const matching = matchLabResultsForEnrichment(documentResults, extracted.items);
      if (!matching.matches.length) {
        throw new Error(`Не удалось однозначно сопоставить показатели. Требуют ручной проверки: ${matching.unmatchedResultIds.length}.`);
      }

      const accepted = confirm(
        `Документ распознан повторно.\n\nБудет обновлено названий: ${matching.matches.length}\nПропущено как неоднозначные: ${matching.unmatchedResultIds.length}\n\nЗначения, даты, единицы, референсы и графики не изменятся. Продолжить?`,
      );
      if (!accepted) return { updated: 0, skipped: documentResults.length };

      for (const match of matching.matches) {
        await enrichCloudLabResultNames({
          id: match.resultId,
          originalName: match.originalName,
          russianName: match.russianName,
          internationalName: match.internationalName,
          canonicalCode: match.canonicalCode,
          aliases: match.aliases,
        });
      }

      const patches = new Map(matching.matches.map(match => [match.resultId, match]));
      setData(current => ({
        ...current,
        labResults: current.labResults.map(result => {
          const patch = patches.get(result.id);
          return patch ? {
            ...result,
            analyte: patch.originalName,
            originalName: patch.originalName,
            russianName: patch.russianName,
            internationalName: patch.internationalName,
            canonicalCode: patch.canonicalCode,
            aliases: patch.aliases,
          } : result;
        }),
      }));
      return { updated: matching.matches.length, skipped: matching.unmatchedResultIds.length };
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const continueDocumentProcessing = async (documentId: string): Promise<number> => {
    if (!cloud) throw new Error('Обработка документа доступна только в облачном режиме');
    setBusy(true);
    setCloudError('');
    try {
      // The Edge Function restores durable batch state by documentId. It reads
      // the original directly from private Storage; no File/Base64 is sent.
      const extraction = await withWakeLock(() => processLabDocument(documentId, getAnthropicModel()));
      setDocuments(await loadLabDocuments(cloud.householdId));
      return extraction.items.length;
    } catch (error) {
      const message = publicProcessingError(errorText(error)) || 'Не удалось обработать документ. Повторите попытку позже.';
      setCloudError(message);
      throw new Error(message);
    } finally {
      setBusy(false);
    }
  };

  const updateLabResult = async (result: LabResult) => {
    setBusy(true);
    setCloudError('');
    try {
      if (cloud) await updateCloudLabResult(result);
      setData(current => ({ ...current, labResults: current.labResults.map(item => item.id === result.id ? result : item) }));
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const removeLabResult = async (id: string) => {
    setBusy(true);
    setCloudError('');
    try {
      if (cloud) await softDeleteCloudLabResult(id);
      setData(current => ({ ...current, labResults: current.labResults.filter(item => item.id !== id) }));
    } catch (error) {
      setCloudError(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const removeLabResultsForOwner = async (owner: Profile): Promise<number> => {
    if (!cloud) throw new Error('Удаление доступно только в облачном режиме');
    setBusy(true);
    try {
      const deleted = await softDeleteCloudLabResultsForOwner(owner);
      const labResults = await loadCloudLabResults(cloud.householdId);
      setData(current => ({ ...current, labResults }));
      return deleted;
    } finally { setBusy(false); }
  };

  const removeLabResultsForDocument = async (documentId: string): Promise<number> => {
    if (!cloud) throw new Error('Удаление доступно только в облачном режиме');
    setBusy(true);
    try {
      const deleted = await softDeleteCloudLabResultsForDocument(documentId);
      const labResults = await loadCloudLabResults(cloud.householdId);
      setData(current => ({ ...current, labResults }));
      return deleted;
    } finally { setBusy(false); }
  };

  // Corrects the whole batch a document produced at once (e.g. the exam date
  // was wrong for all 17 readings), instead of opening each reading's own
  // edit form one by one. Only takenOn/labName are bulk-editable — never
  // owner/country, which define series identity (see the comment on
  // bulkUpdateLabResultMetadataForDocument).
  const bulkUpdateDocumentMetadata = async (documentId: string, patch: { takenOn?: string; labName?: string }): Promise<number> => {
    if (!cloud) throw new Error('Изменение доступно только в облачном режиме');
    setBusy(true);
    setCloudError('');
    try {
      const updated = await bulkUpdateLabResultMetadataForDocument(documentId, patch);
      await updateLabDocumentMetadata(documentId, patch);
      const [labResults, refreshedDocuments] = await Promise.all([
        loadCloudLabResults(cloud.householdId),
        loadLabDocuments(cloud.householdId),
      ]);
      setData(current => ({ ...current, labResults }));
      setDocuments(refreshedDocuments);
      return updated;
    } catch (error) {
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const removeDocument = async (documentId: string, mode: DocumentDeletionMode) => {
    if (!cloud) throw new Error('Удаление документа доступно только в облачном режиме');
    setBusy(true);
    setCloudError('');
    try {
      const outcome = await deleteLabDocument(documentId, mode);
      const [labResults, cloudDocuments] = await Promise.all([
        loadCloudLabResults(cloud.householdId),
        loadLabDocuments(cloud.householdId),
      ]);
      setData(current => ({ ...current, labResults }));
      setDocuments(cloudDocuments);
      return outcome;
    } catch (error) {
      if (error instanceof DocumentHasLinkedResultsError) {
        // The deletion endpoint is authoritative. Refresh the cache when a
        // second device or an earlier tab has saved results after this screen
        // was opened, then let the dialog request a new explicit decision.
        try {
          const labResults = await loadCloudLabResults(cloud.householdId);
          setData(current => ({ ...current, labResults }));
        } catch {
          // The conflict itself remains actionable even if this refresh fails.
        }
      }
      setCloudError(errorText(error));
      throw error;
    } finally {
      setBusy(false);
    }
  };

  if (starting) return <LoadingScreen />;
  if (recoveryMode) {
    return <SetPasswordScreen onDone={() => setRecoveryMode(false)} />;
  }
  if (isSupabaseConfigured && !session && !localMode) {
    return <AuthScreen error={cloudError} onLocal={() => setLocalMode(true)} />;
  }

  return <div className="app-shell">
    {invite && <div className="modal" role="dialog" aria-modal="true"><section className="form"><h2>Присоединиться к семье?</h2><p>Семья: <b>{invite.householdName}</b></p><p>После подтверждения вы получите доступ к общим данным семьи.</p><button className="primary" onClick={() => void acceptHouseholdInvitationLink(invite.token).then(() => window.location.assign('/')).catch(error => setCloudError(errorText(error)))}>Да, присоединиться</button><button className="secondary" onClick={() => { window.history.replaceState({}, '', window.location.pathname); setInvite(null); }}>Отклонить</button></section></div>}
    <header className="topbar">
      <div className="brand-wrap"><span className="logo">♥</span><div><strong>MedApp</strong><small>{cloud?.householdId ? 'Облачная аптечка' : cloud ? 'Выберите семью в «Ещё»' : 'Локальный режим'}</small></div></div>
      <div className="profile-switch" aria-label="Чьи медицинские данные показаны">
        {(cloud?.members.length ? cloud.members.map(member => ({ id: member.userId, name: member.displayName })) : [{ id: 'anton', name: 'Антон' }, { id: 'anya', name: 'Аня' }]).map(member => <button key={member.id} className={data.activeUser === member.id ? 'active' : ''} onClick={() => setProfile(member.id)}>{member.name}</button>)}
      </div>
    </header>
    {!navigator.onLine && <div className="offline">Нет подключения к интернету</div>}
    {cloudError && <div className="error-banner" role="alert">{cloudError}</div>}
    <main className="content"><Routes>
      <Route path="/dashboard" element={<Dashboard medicines={data.medicines} labResults={data.labResults} documents={documents} profile={data.activeUser} members={cloud?.members || localMembers} />} />
      <Route path="/metrics" element={<Metrics labResults={data.labResults} documents={documents} profile={data.activeUser} members={cloud?.members || localMembers} onSaveReport={saveLabReport} onUploadDocument={uploadReportDocument} busy={busy} connected={Boolean(cloud?.householdId)} />} />
      <Route path="/metrics/compare" element={<CompareMetrics labResults={data.labResults} />} />
      <Route path="/metrics/:key" element={<MetricDetail labResults={data.labResults} members={cloud?.members || localMembers} onSave={saveLabResult} onUpdate={updateLabResult} onRemove={removeLabResult} onAttachDocument={attachDocumentToReading} busy={busy} connected={Boolean(cloud?.householdId)} householdId={cloud?.householdId} userId={cloud?.session.user.id} />} />
      <Route path="/documents" element={<Documents documents={documents} archiveDocuments={archiveDocuments} labResults={data.labResults} profile={data.activeUser} members={cloud?.members || localMembers} connected={Boolean(cloud?.householdId)} busy={busy} onEnrich={enrichDocumentLabResults} onProcess={continueDocumentProcessing} onDelete={removeDocument} onDeleteResults={removeLabResultsForDocument} onBulkUpdateMetadata={bulkUpdateDocumentMetadata} onAddArchive={addArchiveDocument} onDeleteArchive={removeArchiveDocument} />} />
      <Route path="/pharmacy" element={<Pharmacy medicines={data.medicines} members={cloud?.members || localMembers} onSave={saveMedicine} onUpdate={updateMedicine} onRemove={removeMedicine} busy={busy} connected={Boolean(cloud?.householdId)} />} />
      <Route path="/more" element={<More configured={isSupabaseConfigured} connected={Boolean(cloud)} hasHousehold={Boolean(cloud?.householdId)} localMode={localMode} householdId={cloud?.householdId} householdName={cloud?.householdName || ''} householdRole={cloud?.householdRole || null} members={cloud?.members || localMembers} documents={documents} email={session?.user.email || ''} onClaimLegacyProfile={async (legacyOwner, memberUserId) => { await claimLegacyMedicalProfile(legacyOwner, memberUserId); window.location.reload(); }} onDeleteResultsForOwner={removeLabResultsForOwner} onDeleteResultsForDocument={removeLabResultsForDocument} theme={data.settings.theme || 'auto'} onTheme={theme => setData(current => ({ ...current, settings: { ...current.settings, theme } }))} onSignOut={async () => { await signOut(); setLocalMode(false); }} onExitLocalMode={() => setLocalMode(false)} />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes></main>
    <nav className="bottom-nav">{nav.map(item => <NavLink key={item.to} to={item.to} className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'}><span>{item.icon}</span><small>{item.label}</small></NavLink>)}</nav>
  </div>;
}

function LoadingScreen() {
  return <main className="auth-shell"><section className="auth-card empty"><span className="logo auth-logo">♥</span><h1>MedApp</h1><p>Подключаем защищённую аптечку…</p></section></main>;
}

function AuthScreen({ error, onLocal }: { error: string; onLocal: () => void }) {
  const [mode, setMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [localError, setLocalError] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setLocalError(''); setMessage('');
    try {
      if (mode === 'reset') {
        await requestPasswordReset(email);
        setMessage('Письмо со ссылкой для установки пароля отправлено — проверьте почту (и папку "Спам").');
      } else if (mode === 'signup') {
        const session = await signUpWithPassword(email, password, displayName);
        if (!session) setMessage('Проверьте почту и подтвердите email — после этого сможете войти с этим паролем.');
      } else {
        await signInWithPassword(email, password);
      }
    } catch (cause) { setLocalError(errorText(cause)); }
    finally { setBusy(false); }
  };

  return <main className="auth-shell"><section className="auth-card">
    <span className="logo auth-logo">♥</span><p className="eyebrow">Семейная аптечка</p><h1>Вход в MedApp</h1>
    <p>{mode === 'signup' ? 'Придумайте пароль — он понадобится для входа в следующий раз.' : mode === 'reset' ? 'Укажите email — пришлём ссылку для установки пароля.' : 'Введите email и пароль.'}</p>
    <form className="form auth-form" onSubmit={submit}>
      <label>Email<input type="email" value={email} onChange={event => setEmail(event.target.value)} required autoComplete="email" placeholder="name@example.com" /></label>
      {mode === 'signup' && <label>Ваше имя<input value={displayName} onChange={event => setDisplayName(event.target.value)} required autoComplete="name" placeholder="Например, Анна" /></label>}
      {mode !== 'reset' && <label>Пароль<input type="password" value={password} onChange={event => setPassword(event.target.value)} required minLength={6} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} placeholder="••••••••" /></label>}
      {(localError || error) && <p className="form-error" role="alert">{localError || error}</p>}
      {message && <p className="form-success">{message}</p>}
      <button className="primary" type="submit" disabled={busy}>{busy ? 'Подождите…' : mode === 'signup' ? 'Создать аккаунт' : mode === 'reset' ? 'Отправить ссылку' : 'Войти'}</button>
    </form>
    <button className="text-button" onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setLocalError(''); setMessage(''); }}>
      {mode === 'signup' ? 'У меня уже есть пароль — войти' : 'Первый раз здесь — создать пароль'}
    </button>
    {mode !== 'reset' && <button className="text-button" onClick={() => { setMode('reset'); setLocalError(''); setMessage(''); }}>Забыли пароль или не получается войти?</button>}
    {mode === 'reset' && <button className="text-button" onClick={() => { setMode('signin'); setLocalError(''); setMessage(''); }}>Назад ко входу</button>}
    <button className="text-button" onClick={onLocal}>Открыть локальную версию без входа</button>
  </section></main>;
}

function SetPasswordScreen({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setLocalError('');
    try {
      await setNewPassword(password);
      onDone();
    } catch (cause) { setLocalError(errorText(cause)); setBusy(false); }
  };

  return <main className="auth-shell"><section className="auth-card">
    <span className="logo auth-logo">♥</span><p className="eyebrow">Семейная аптечка</p><h1>Новый пароль</h1>
    <p>Придумайте пароль — дальше входите им.</p>
    <form className="form auth-form" onSubmit={submit}>
      <label>Новый пароль<input type="password" value={password} onChange={event => setPassword(event.target.value)} required minLength={6} autoComplete="new-password" placeholder="••••••••" autoFocus /></label>
      {localError && <p className="form-error" role="alert">{localError}</p>}
      <button className="primary" type="submit" disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить пароль'}</button>
    </form>
  </section></main>;
}

function pluralize(value: number, one: string, few: string, many: string): string {
  const mod100 = value % 100;
  const mod10 = value % 10;
  if (mod100 > 10 && mod100 < 20) return many;
  if (mod10 === 1) return one;
  if (mod10 > 1 && mod10 < 5) return few;
  return many;
}

type PharmacyAttentionFilter = 'all' | 'expired' | 'expiring' | 'low-stock' | 'missing-expiry';
type AttentionItem = { id: string; title: string; detail: string; tone?: 'danger' | 'warn'; onOpen: () => void };
const PHARMACY_ATTENTION_FILTERS: Array<{ value: PharmacyAttentionFilter; label: string }> = [
  { value: 'all', label: 'Все сроки' },
  { value: 'expired', label: 'Просроченные' },
  { value: 'expiring', label: 'Истекают ≤30 дн.' },
  { value: 'low-stock', label: 'Мало осталось' },
  { value: 'missing-expiry', label: 'Нет срока' },
];

function Dashboard({ medicines, labResults, documents, profile, members }: { medicines: Medicine[]; labResults: LabResult[]; documents: MedicalDocument[]; profile: Profile; members: FamilyMember[] }) {
  const navigate = useNavigate();
  const expired = medicines.filter(m => expiryStatus(m.expiry) === 'expired').sort((a, b) => (a.expiry || '').localeCompare(b.expiry || ''));
  const expiring = medicines.filter(m => expiryStatus(m.expiry) === 'expiring').sort((a, b) => (daysUntilExpiry(a.expiry) || 0) - (daysUntilExpiry(b.expiry) || 0));
  const lowStock = medicines.filter(m => {
    const amount = Number(m.amount);
    const threshold = Number(m.threshold);
    return m.threshold !== undefined && m.threshold !== '' && Number.isFinite(amount) && Number.isFinite(threshold) && amount <= threshold;
  });
  const missingExpiry = medicines.filter(m => expiryStatus(m.expiry) === 'missing');
  const ownDocuments = documents.filter(document => document.owner === profile);
  const documentsToReview = ownDocuments.filter(document => document.reviewCount > 0 || document.processingStatus === 'failed');
  const ownResults = labResults.filter(r => r.owner === profile);
  const attentionCount = expired.length + expiring.length + lowStock.length + missingExpiry.length + documentsToReview.length;
  const goToPharmacy = (filter: PharmacyAttentionFilter) => navigate(`/pharmacy?filter=${filter}`);
  const attentionItems: AttentionItem[] = [
    ...expired.map(medicine => ({ id: `expired-${medicine.id}`, title: medicine.name, detail: `Срок истёк ${new Date(medicine.expiry || '').toLocaleDateString('ru-RU')}`, tone: 'danger' as const, onOpen: () => goToPharmacy('expired') })),
    ...expiring.map(medicine => ({ id: `expiring-${medicine.id}`, title: medicine.name, detail: daysUntilExpiry(medicine.expiry) === 0 ? 'Срок истекает сегодня' : `Срок истекает через ${daysUntilExpiry(medicine.expiry)} дн.`, tone: 'warn' as const, onOpen: () => goToPharmacy('expiring') })),
    ...lowStock.map(medicine => ({ id: `low-stock-${medicine.id}`, title: medicine.name, detail: `Осталось ${medicine.amount} ${medicine.unit || 'шт.'} при пороге ${medicine.threshold}`, onOpen: () => goToPharmacy('low-stock') })),
    ...(missingExpiry.length ? [{ id: 'missing-expiry', title: `${missingExpiry.length} ${pluralize(missingExpiry.length, 'лекарство', 'лекарства', 'лекарств')}`, detail: 'Срок годности не указан', onOpen: () => goToPharmacy('missing-expiry') }] : []),
    ...(documentsToReview.length ? [{ id: 'documents-to-review', title: `${documentsToReview.length} ${pluralize(documentsToReview.length, 'документ', 'документа', 'документов')}`, detail: 'Нужно проверить или обработать', onOpen: () => navigate('/documents') }] : []),
  ];
  const visibleAttentionItems = attentionItems.slice(0, 3);

  return <div className="stack dashboard-stack">
    <section className="hero">
      <p className="eyebrow">Сегодня · <span>{profileName(profile, members)}</span></p>
      {attentionItems[0]
        ? <h1 style={{ fontSize: 22, color: attentionItems[0].tone === 'danger' ? 'var(--danger)' : attentionItems[0].tone === 'warn' ? 'var(--warn)' : undefined }}>{attentionItems[0].title} — {attentionItems[0].detail.toLowerCase()}</h1>
        : <h1 style={{ fontSize: 22 }}>Всё в порядке, задач нет</h1>}
      {attentionCount > 1 && <p>Ещё {attentionCount - 1} {pluralize(attentionCount - 1, 'пункт', 'пункта', 'пунктов')} ниже.</p>}
    </section>
    <section className="panel attention-panel"><div className="row"><div><p className="eyebrow">Требует внимания</p><h2>Аптечка</h2></div><button className="text-button" onClick={() => navigate('/pharmacy')}>Открыть аптечку</button></div>{attentionCount ? <div className="attention-list">
      {visibleAttentionItems.map(item => <button className={`attention-row ${item.tone || ''}`} key={item.id} onClick={item.onOpen}><span><b>{item.title}</b><small>{item.detail}</small></span><span aria-hidden="true">›</span></button>)}
      {attentionItems.length > visibleAttentionItems.length && <button className="all-attention-button" onClick={() => navigate('/pharmacy')}>Все задачи аптечки <span aria-hidden="true">›</span></button>}
    </div> : <p className="attention-empty">Просроченных лекарств, низких остатков и документов для проверки нет.</p>}</section>
    <div className="dashboard-secondary"><section className="stats dashboard-stats" aria-label="Состояние данных"><article><strong>{medicines.length}</strong><span>лекарств</span></article><article><strong>{ownResults.length}</strong><span>показателей</span></article><article><strong>{ownDocuments.length}</strong><span>документов</span></article></section>
      <section className="panel"><p className="eyebrow">Быстрые действия</p><h2>Что добавить?</h2><div className="actions"><button onClick={() => navigate('/pharmacy')}>Добавить лекарство</button><button onClick={() => navigate('/metrics')}>Добавить показатель</button></div></section></div>
  </div>;
}


// A compact "⋯" action menu — collapses a row's secondary actions (edit,
// delete, open file, ...) behind one button instead of a cluster of icons
// competing with the row's own content for space. Shared by the archive
// card list and the per-reading rows in MetricDetail.
function OverflowMenu({ label = 'Ещё действия', items }: { label?: string; items: { label: string; onClick: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  return <div className="overflow-menu">
    <button type="button" className="ghost overflow-menu-trigger" aria-label={label} aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(v => !v)}>⋯</button>
    {open && <>
      <div className="overflow-menu-backdrop" onClick={() => setOpen(false)} />
      <div className="overflow-menu-list" role="menu">
        {items.map((item, index) => <button key={index} type="button" role="menuitem" className={item.danger ? 'danger-text' : undefined} onClick={() => { setOpen(false); item.onClick(); }}>{item.label}</button>)}
      </div>
    </>}
  </div>;
}

function Documents({ documents, archiveDocuments, labResults, profile, members, connected, busy, onEnrich, onProcess, onDelete, onDeleteResults, onBulkUpdateMetadata, onAddArchive, onDeleteArchive }: {
  documents: MedicalDocument[];
  archiveDocuments: MedicalDocument[];
  labResults: LabResult[];
  profile: Profile;
  members: FamilyMember[];
  connected: boolean;
  busy: boolean;
  onEnrich: (document: MedicalDocument) => Promise<{ updated: number; skipped: number }>;
  onProcess: (documentId: string) => Promise<number>;
  onDelete: (documentId: string, mode: DocumentDeletionMode) => Promise<DocumentDeletionOutcome>;
  onDeleteResults: (documentId: string) => Promise<number>;
  onBulkUpdateMetadata: (documentId: string, patch: { takenOn?: string; labName?: string }) => Promise<number>;
  onAddArchive: (input: { file: File; owner: Profile; category: ArchiveCategory; title: string; note?: string; takenOn?: string }) => Promise<void>;
  onDeleteArchive: (documentId: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: 'analyses' | 'archive' = searchParams.get('tab') === 'archive' ? 'archive' : 'analyses';
  const setTab = (next: 'analyses' | 'archive') => setSearchParams(next === 'analyses' ? {} : { tab: next }, { replace: true });
  const [highlightId, setHighlightId] = useState(() => searchParams.get('highlight') || '');
  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(''), 2500);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const [query, setQuery] = useState('');
  const [labFilter, setLabFilter] = useState('');
  const [openingId, setOpeningId] = useState('');
  const [sharingId, setSharingId] = useState('');
  const [enrichingId, setEnrichingId] = useState('');
  const [enrichMessage, setEnrichMessage] = useState('');
  const [processingId, setProcessingId] = useState('');
  const [processingMessage, setProcessingMessage] = useState('');
  const [diagnostics, setDiagnostics] = useState<Record<string, DocumentProcessingDiagnostics | null>>({});
  const [loadingDiagnosticsId, setLoadingDiagnosticsId] = useState('');
  const [openError, setOpenError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ document: MedicalDocument; readingCount: number } | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteConflict, setDeleteConflict] = useState('');
  const [deleting, setDeleting] = useState<'document' | 'results' | 'all' | ''>('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [bulkEditTarget, setBulkEditTarget] = useState<MedicalDocument | null>(null);
  const [bulkEditForm, setBulkEditForm] = useState({ takenOn: '', labName: '' });
  const [bulkEditBusy, setBulkEditBusy] = useState(false);
  const [bulkEditError, setBulkEditError] = useState('');
  const [bulkEditMessage, setBulkEditMessage] = useState('');
  const ownDocuments = useMemo(() => documents.filter(document => document.owner === profile), [documents, profile]);
  // Distinct lab names actually present among this person's documents —
  // lets "открыл Maccabi и посмотрел, что от неё пришло" be a filter
  // instead of a hopeful guess in the free-text search below.
  const documentLabs = useMemo(() => {
    const set = new Set<string>();
    for (const document of ownDocuments) if (document.labName) set.add(document.labName);
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [ownDocuments]);
  const filtered = useMemo(() => ownDocuments
    .filter(document => !labFilter || document.labName === labFilter)
    .filter(document => matchesLabDocumentSearch(document, labResults, query)), [ownDocuments, labResults, query, labFilter]);

  const [archiveQuery, setArchiveQuery] = useState('');
  const [archiveCategory, setArchiveCategory] = useState<ArchiveCategory | 'all'>('all');
  const [showAddArchive, setShowAddArchive] = useState(false);
  const [archiveFile, setArchiveFile] = useState<File | null>(null);
  const [archiveTitle, setArchiveTitle] = useState('');
  const [archiveTitleEdited, setArchiveTitleEdited] = useState(false);
  const [archiveDocCategory, setArchiveDocCategory] = useState<ArchiveCategory>('prescription');
  const [archiveOwner, setArchiveOwner] = useState<Profile>(profile);
  const [archiveTakenOn, setArchiveTakenOn] = useState('');
  const [archiveNote, setArchiveNote] = useState('');
  const [archiveError, setArchiveError] = useState('');
  const [deletingArchiveId, setDeletingArchiveId] = useState('');

  const ownArchiveDocuments = useMemo(() => archiveDocuments.filter(document => document.owner === profile), [archiveDocuments, profile]);
  const filteredArchive = useMemo(() => {
    const normalized = archiveQuery.trim().toLowerCase();
    return ownArchiveDocuments
      .filter(document => archiveCategory === 'all' || document.category === archiveCategory)
      .filter(document => !normalized || (document.title || document.originalName).toLowerCase().includes(normalized));
  }, [ownArchiveDocuments, archiveQuery, archiveCategory]);

  const resetAddArchive = () => {
    setShowAddArchive(false);
    setArchiveFile(null);
    setArchiveTitle('');
    setArchiveTitleEdited(false);
    setArchiveDocCategory('prescription');
    setArchiveOwner(profile);
    setArchiveTakenOn('');
    setArchiveNote('');
    setArchiveError('');
  };

  const handleArchiveFile = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (!picked) return;
    setArchiveFile(picked);
    if (!archiveTitleEdited) setArchiveTitle(picked.name.replace(/\.[^.]+$/, ''));
  };

  const submitAddArchive = async (event: FormEvent) => {
    event.preventDefault();
    setArchiveError('');
    if (!archiveFile || !archiveTitle.trim()) {
      setArchiveError('Выберите файл и укажите название.');
      return;
    }
    try {
      await onAddArchive({
        file: archiveFile,
        owner: archiveOwner,
        category: archiveDocCategory,
        title: archiveTitle.trim(),
        note: archiveNote.trim() || undefined,
        takenOn: archiveTakenOn || undefined,
      });
      resetAddArchive();
    } catch (error) {
      setArchiveError(errorText(error));
    }
  };

  const deleteArchiveDocument = async (document: MedicalDocument) => {
    if (!confirm(`Удалить «${document.title || document.originalName}» из архива?`)) return;
    setDeletingArchiveId(document.id);
    setArchiveError('');
    try {
      await onDeleteArchive(document.id);
    } catch (error) {
      setArchiveError(errorText(error));
    } finally {
      setDeletingArchiveId('');
    }
  };

  const enrichDocument = async (document: MedicalDocument) => {
    setEnrichingId(document.id);
    setEnrichMessage('');
    setOpenError('');
    try {
      const result = await onEnrich(document);
      if (result.updated) setEnrichMessage(`Обновлено показателей: ${result.updated}. Пропущено: ${result.skipped}.`);
    } catch (error) {
      setOpenError(errorText(error));
    } finally {
      setEnrichingId('');
    }
  };

  const openDocument = async (documentId: string) => {
    setOpeningId(documentId);
    setOpenError('');
    try { await openLabSourceDocument(documentId); }
    catch (error) { setOpenError(errorText(error)); }
    finally { setOpeningId(''); }
  };

  const shareDocument = async (document: MedicalDocument) => {
    setSharingId(document.id);
    setOpenError('');
    try {
      const outcome = await shareLabSourceDocument(document.id, document.originalName);
      if (outcome === 'downloaded') setProcessingMessage('Браузер не поддерживает системное меню — оригинал сохранён в загрузках.');
    } catch (error) {
      // Closing iOS/Android's share sheet is an expected cancellation, not an
      // error that should alarm the user.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setOpenError(errorText(error));
    } finally {
      setSharingId('');
    }
  };

  const loadDiagnostics = async (documentId: string) => {
    if (loadingDiagnosticsId === documentId || Object.hasOwn(diagnostics, documentId)) return;
    setLoadingDiagnosticsId(documentId);
    try {
      const loaded = await loadDocumentProcessingDiagnostics(documentId);
      setDiagnostics(current => ({ ...current, [documentId]: loaded }));
    } catch {
      // Technical details are supplementary. Keep the normal passport usable
      // and avoid exposing a provider/database error to the patient.
      setDiagnostics(current => ({ ...current, [documentId]: null }));
    } finally {
      setLoadingDiagnosticsId('');
    }
  };

  // Suggestions for the bulk-edit modal's "Лаборатория" field, scoped to the
  // target document's own country.
  const bulkEditLabs = useMemo(() => {
    if (!bulkEditTarget) return [];
    return knownLabInstitutions([...labResults, ...documents], bulkEditTarget.country || 'other');
  }, [labResults, documents, bulkEditTarget]);

  const openBulkEdit = (document: MedicalDocument) => {
    setBulkEditTarget(document);
    setBulkEditForm({ takenOn: document.takenOn || '', labName: document.labName || '' });
    setBulkEditError('');
  };
  const closeBulkEdit = () => { setBulkEditTarget(null); setBulkEditError(''); };

  const submitBulkEdit = async (event: FormEvent, readingCount: number) => {
    event.preventDefault();
    if (!bulkEditTarget) return;
    setBulkEditError('');
    if (!bulkEditForm.takenOn) { setBulkEditError('Укажите дату анализа.'); return; }
    const accepted = confirm(`Изменится у всех показателей этого документа (${readingCount}):\n\nДата: ${new Date(bulkEditForm.takenOn).toLocaleDateString('ru-RU')}\nЛаборатория: ${bulkEditForm.labName.trim() || 'не указана'}\n\nПродолжить?`);
    if (!accepted) return;
    setBulkEditBusy(true);
    try {
      const updated = await onBulkUpdateMetadata(bulkEditTarget.id, { takenOn: bulkEditForm.takenOn, labName: bulkEditForm.labName.trim() });
      setBulkEditMessage(`Обновлено показателей: ${updated}.`);
      setBulkEditTarget(null);
    } catch (error) {
      setBulkEditError(errorText(error));
    } finally {
      setBulkEditBusy(false);
    }
  };

  const runProcessing = async (documentId: string, action: 'retry' | 'continue') => {
    setProcessingId(documentId);
    setProcessingMessage('');
    setOpenError('');
    try {
      const itemCount = await onProcess(documentId);
      setProcessingMessage(itemCount
        ? `Распознавание завершено: найдено показателей — ${itemCount}. Откройте «Показатели», чтобы проверить и сохранить их.`
        : 'Обработка завершена. Откройте «Показатели», чтобы проверить результат.');
      const refreshed = await loadDocumentProcessingDiagnostics(documentId);
      setDiagnostics(current => ({ ...current, [documentId]: refreshed }));
    } catch (error) {
      setOpenError(publicProcessingError(errorText(error)) || `Не удалось ${action === 'retry' ? 'повторить' : 'продолжить'} обработку документа.`);
    } finally {
      setProcessingId('');
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirmation('');
    setDeleteConflict('');
  };

  const deleteOnlyResults = async () => {
    if (!deleteTarget) return;
    setDeleting('results');
    try {
      const count = await onDeleteResults(deleteTarget.document.id);
      setDeleteMessage(`Удалено показателей: ${count}. Исходный файл сохранён.`);
      setDeleteTarget(null);
      setDeleteConfirmation('');
    } catch (error) {
      if (error instanceof DocumentHasLinkedResultsError) {
        setDeleteTarget(current => current && ({ ...current, readingCount: error.linkedResultCount }));
        setDeleteConfirmation('');
        setDeleteConflict(`Состояние обновлено с сервера: с файлом связаны подтверждённые показатели (${error.linkedResultCount}). Это не черновик распознавания. Удаление остановлено — проверьте число и при необходимости подтвердите удаление всего.`);
      } else {
        setOpenError(errorText(error));
      }
    } finally {
      setDeleting('');
    }
  };

  const deleteDocument = async (mode: DocumentDeletionMode) => {
    if (!deleteTarget) return;
    setDeleting(mode === 'document_only' ? 'document' : 'all');
    try {
      const outcome = await onDelete(deleteTarget.document.id, mode);
      setDeleteMessage(`Документ удалён${outcome.deletedResultCount ? ` вместе с показателями: ${outcome.deletedResultCount}` : ''}.${outcome.storageCleanupPending ? ' Оригинал больше недоступен в приложении; для объекта требуется служебная очистка хранилища.' : ''}`);
      setDeleteTarget(null);
      setDeleteConfirmation('');
    } catch (error) {
      setOpenError(errorText(error));
    } finally {
      setDeleting('');
    }
  };

  if (!connected) return <div className="stack"><section className="page-head"><p className="eyebrow">Медицинский архив</p><h1>Документы</h1><p>Архив исходных PDF, фотографий анализов и других медицинских файлов доступен после входа в облачную версию.</p></section></div>;

  return <div className="stack">
    <section className="page-head">
      <div className="row">
        <div>
          <p className="eyebrow">{profileName(profile, members)}</p>
          <h1>Документы</h1>
          <p>{tab === 'analyses' ? 'Исходные PDF и фотографии анализов. Каждый файл хранится один раз.' : 'Рецепты, направления, выписки и другие медицинские файлы — без привязки к конкретному анализу.'}</p>
        </div>
        {tab === 'archive' && <div className="page-head-actions"><button type="button" className="primary round" aria-label="Добавить в архив" onClick={() => setShowAddArchive(true)}>＋</button></div>}
      </div>
      <div className="chips" style={{ marginTop: 16 }}>
        <button type="button" className={tab === 'analyses' ? 'active' : ''} onClick={() => setTab('analyses')}>Анализы</button>
        <button type="button" className={tab === 'archive' ? 'active' : ''} onClick={() => setTab('archive')}>Архив</button>
      </div>
    </section>

    {tab === 'analyses' && <>
    {ownDocuments.length > 0 && <div className="row" style={{ gap: 8 }}>
      <input className="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Файл, показатель, лаборатория или дата" aria-label="Поиск документов и показателей в них" />
      {documentLabs.length > 0 && <select value={labFilter} onChange={event => setLabFilter(event.target.value)} aria-label="Фильтр по лаборатории">
        <option value="">Все лаборатории</option>
        {documentLabs.map(lab => <option key={lab} value={lab}>{lab}</option>)}
      </select>}
    </div>}
    {openError && <p className="form-error" role="alert">{openError}</p>}
    {enrichMessage && <p className="form-success" role="status">{enrichMessage}</p>}
    {processingMessage && <p className="form-success" role="status">{processingMessage}</p>}
    {deleteMessage && <p className="form-success" role="status">{deleteMessage}</p>}
    {bulkEditMessage && <p className="form-success" role="status">{bulkEditMessage}</p>}
    <section className="lab-grid">{filtered.map(document => {
      const readings = labResults.filter(result => result.documentId === document.id);
      const technical = diagnostics[document.id];
      const outOfRangeReadings = readings.filter(result => result.value !== undefined && inRange(result.value, result.refLow, result.refHigh) === false);
      const outOfRange = outOfRangeReadings.length;
      const statusClass = document.processingStatus === 'failed' ? 'badge danger'
        : document.processingStatus === 'ready' ? 'badge ok' : 'badge';
      return <article className={document.id === highlightId ? 'lab-card document-passport highlight' : 'lab-card document-passport'} key={document.id}>
        <div className="row"><div><p className="eyebrow">Паспорт документа</p><h2>Лабораторный анализ</h2></div><span className={statusClass}>{DOCUMENT_PROCESSING_STATUS_LABELS[document.processingStatus]}</span></div>
        <div className="stack" style={{ gap: 6 }}>
          <p><b>Дата анализа:</b> {formatOptionalDocumentDate(document.takenOn)}</p>
          <p><b>Лаборатория:</b> {document.labName || 'не указана'}</p>
          <p><b>Страна:</b> {COUNTRY_LABELS[document.country || 'other']}</p>
          <p><b>Пациент:</b> {profileName(document.owner, members)}</p>
          <p><b>Файл:</b> {document.originalName}</p>
          <p><b>Размер:</b> {formatFileSize(document.sizeBytes)}{document.pageCount ? ` · ${document.pageCount} стр.` : ''}{document.batchCount ? ` · ${document.batchCount} пак.` : ''}</p>
          <p><b>Начато:</b> {formatOptionalDocumentDate(document.processingStartedAt)}</p>
          <p><b>Завершено:</b> {formatOptionalDocumentDate(document.processingFinishedAt)}</p>
        </div>
        {['queued', 'processing'].includes(document.processingStatus) && <div className="stack" style={{ gap: 6 }}>
          <progress value={document.processingProgress} max={100} style={{ width: '100%' }} />
          <small>Обработка: {document.processingProgress}%</small>
        </div>}
        <div className="document-counters">
          <span>Найдено: <b>{document.extractedCount || readings.length}</b></span>
          <span>Подтверждено: <b>{document.confirmedCount || readings.length}</b></span>
          {document.reviewCount > 0 && <span>Проверить: <b>{document.reviewCount}</b></span>}
          {document.failedCount > 0 && <span>Ошибок: <b>{document.failedCount}</b></span>}
        </div>
        {document.laboratoryConclusion && <section className="document-conclusion" aria-label="Заключение лаборатории">
          <div className="row"><p className="eyebrow">Заключение лаборатории</p>{document.laboratoryConclusionPage && <small>Страница {document.laboratoryConclusionPage}</small>}</div>
          <p>{document.laboratoryConclusion}</p>
          <small>Дословный текст из исходного бланка, не рекомендация приложения.</small>
        </section>}
        {document.lastProcessingError && <p className="form-error" role="alert">{publicProcessingError(document.lastProcessingError) || 'Не удалось обработать документ. Повторите попытку позже.'}</p>}
        <footer><span>{readings.length} показателей{outOfRange ? ` · ${outOfRange} вне диапазона` : ''}</span><span>Алгоритм {document.processorVersion || 'не указан'}</span></footer>
        {outOfRangeReadings.length > 0 && <details>
          <summary>Вне диапазона ({outOfRangeReadings.length})</summary>
          <div className="attention-list">
            {outOfRangeReadings.map(result => <button type="button" className="attention-row warn" key={result.id} onClick={() => navigate(`/metrics/${labSeriesKey(result.analyte, result.country, result.owner, result.canonicalCode, undefined, result.labName)}`)}>
              <span><b>{result.originalName || result.analyte}</b><small>{result.value}{result.unit ? ` ${result.unit}` : ''}{result.refLow !== undefined && result.refHigh !== undefined ? ` · норма ${result.refLow}–${result.refHigh}` : ''}</small></span>
              <span aria-hidden="true">›</span>
            </button>)}
          </div>
        </details>}
        {document.reviewCount > 0 && <button type="button" className="primary" disabled={busy} onClick={() => navigate(`/metrics?reviewDocument=${encodeURIComponent(document.id)}`)}>Проверить и сохранить показатели ({document.reviewCount})</button>}
        <div className="document-file-actions">
          <button type="button" className="secondary" disabled={openingId === document.id || enrichingId === document.id || sharingId === document.id} onClick={() => void openDocument(document.id)}>{openingId === document.id ? 'Открываем…' : '📄 Открыть оригинал'}</button>
          <button type="button" className="secondary icon-button" disabled={openingId === document.id || enrichingId === document.id || sharingId === document.id} onClick={() => void shareDocument(document)} aria-label="Поделиться оригиналом анализа" title="Поделиться оригиналом анализа">
            {sharingId === document.id ? <span className="loading-spinner" aria-label="Готовим файл" /> : <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M12 16V3m0 0 4 4m-4-4-4 4M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
          </button>
        </div>
        {readings.length > 0 && <button type="button" className="secondary" disabled={busy || enrichingId === document.id} onClick={() => void enrichDocument(document)}>{enrichingId === document.id ? 'Распознаём и сопоставляем…' : '↻ Обновить названия из документа'}</button>}
        {readings.length > 0 && <button type="button" className="secondary" disabled={busy} onClick={() => openBulkEdit(document)}>✎ Изменить дату/лабораторию у всех показателей ({readings.length})</button>}
        <button type="button" className="secondary danger-button" disabled={busy} onClick={() => { setDeleteTarget({ document, readingCount: readings.length }); setDeleteConfirmation(''); setDeleteConflict(''); setOpenError(''); }}>Удалить документ</button>
        <details onToggle={event => { if (event.currentTarget.open) void loadDiagnostics(document.id); }}>
          <summary>Техническая информация</summary>
          {loadingDiagnosticsId === document.id && <p><small>Загружаем состояние обработки…</small></p>}
          {Object.hasOwn(diagnostics, document.id) && !diagnostics[document.id] && <p><small>Для этого документа ещё нет задачи обработки.</small></p>}
          {technical && <DocumentTechnicalInfo diagnostics={technical} processing={processingId === document.id || busy} onRetry={() => void runProcessing(document.id, 'retry')} onContinue={() => void runProcessing(document.id, 'continue')} />}
        </details>
      </article>;
    })}</section>
    {!ownDocuments.length && <section className="panel empty"><h2>Документов пока нет</h2><p>Добавьте старый исходный PDF или загрузите новый анализ.</p></section>}
    {ownDocuments.length > 0 && !filtered.length && <section className="panel empty"><h2>Ничего не найдено</h2><p>Измените поисковый запрос.</p></section>}
    </>}

    {tab === 'archive' && <>
    {ownArchiveDocuments.length > 0 && <input className="search" value={archiveQuery} onChange={event => setArchiveQuery(event.target.value)} placeholder="Поиск по названию" />}
    {ownArchiveDocuments.length > 0 && <div className="chips">
      <button type="button" className={archiveCategory === 'all' ? 'active' : ''} onClick={() => setArchiveCategory('all')}>Все</button>
      {(Object.keys(ARCHIVE_CATEGORY_LABELS) as ArchiveCategory[]).map(cat => <button key={cat} type="button" className={archiveCategory === cat ? 'active' : ''} onClick={() => setArchiveCategory(cat)}>{ARCHIVE_CATEGORY_LABELS[cat]}</button>)}
    </div>}
    {archiveError && <p className="form-error" role="alert">{archiveError}</p>}
    <section className="lab-grid">{filteredArchive.map(document => <article className={document.id === highlightId ? 'lab-card archive-card highlight' : 'lab-card archive-card'} key={document.id}>
      <div className="row">
        <div><p className="eyebrow">{ARCHIVE_CATEGORY_LABELS[document.category || 'other']}</p><h2>{document.title || document.originalName}</h2></div>
        <OverflowMenu items={[
          { label: '📄 Открыть оригинал', onClick: () => void openDocument(document.id) },
          { label: '✕ Удалить', onClick: () => void deleteArchiveDocument(document), danger: true },
        ]} />
      </div>
      <div className="stack" style={{ gap: 6 }}>
        {document.takenOn && <p><b>Дата:</b> {formatOptionalDocumentDate(document.takenOn)}</p>}
        <p><b>Пациент:</b> {profileName(document.owner, members)}</p>
        {document.note && <p>{document.note}</p>}
        <p><b>Файл:</b> {document.originalName} · {formatFileSize(document.sizeBytes)}</p>
      </div>
      {(openingId === document.id || deletingArchiveId === document.id) && <p><small>{deletingArchiveId === document.id ? 'Удаляем…' : 'Открываем…'}</small></p>}
    </article>)}</section>
    {!ownArchiveDocuments.length && <section className="panel empty"><h2>В архиве пока пусто</h2><p>Добавьте рецепт, направление или другой медицинский документ.</p></section>}
    {ownArchiveDocuments.length > 0 && !filteredArchive.length && <section className="panel empty"><h2>Ничего не найдено</h2><p>Измените поисковый запрос или категорию.</p></section>}
    </>}

    {showAddArchive && <div className="modal" role="dialog" aria-modal="true"><form className="form" onSubmit={submitAddArchive}>
      <div className="row"><div><p className="eyebrow">Архив</p><h2>Добавить документ</h2></div><button type="button" className="ghost" onClick={resetAddArchive}>×</button></div>
      <label>Файл<span className="file-picker-button">{archiveFile?.name || 'Выбрать файл'}<input type="file" accept="application/pdf,image/*" onChange={handleArchiveFile} /></span></label>
      <label>Название<input value={archiveTitle} onChange={event => { setArchiveTitle(event.target.value); setArchiveTitleEdited(true); }} required /></label>
      <div className="chips">
        {(Object.keys(ARCHIVE_CATEGORY_LABELS) as ArchiveCategory[]).map(cat => <button key={cat} type="button" className={archiveDocCategory === cat ? 'active' : ''} onClick={() => setArchiveDocCategory(cat)}>{ARCHIVE_CATEGORY_LABELS[cat]}</button>)}
      </div>
      <div className="two">
        <label>Владелец<select value={archiveOwner} onChange={event => setArchiveOwner(event.target.value as Profile)}>{members.map(member => <option key={member.userId} value={member.userId}>{member.displayName}</option>)}</select></label>
        <label>Дата (необязательно)<input type="date" value={archiveTakenOn} onChange={event => setArchiveTakenOn(event.target.value)} /></label>
      </div>
      <label>Заметка (необязательно)<textarea value={archiveNote} onChange={event => setArchiveNote(event.target.value)} rows={2} /></label>
      {archiveError && <p className="form-error" role="alert">{archiveError}</p>}
      <button className="primary" type="submit" disabled={busy}>{busy ? 'Сохраняем…' : 'Добавить в архив'}</button>
    </form></div>}
    {bulkEditTarget && <div className="modal" role="dialog" aria-modal="true" aria-labelledby="bulk-edit-title">
      <form className="form" onSubmit={event => void submitBulkEdit(event, labResults.filter(r => r.documentId === bulkEditTarget.id).length)}>
        <div className="row"><div><p className="eyebrow">Изменится у всех показателей документа</p><h2 id="bulk-edit-title">{bulkEditTarget.originalName}</h2></div><button type="button" className="ghost" onClick={closeBulkEdit} aria-label="Закрыть">×</button></div>
        <p className="ai-hint">Меняются только дата и лаборатория — у всех {labResults.filter(r => r.documentId === bulkEditTarget.id).length} показателей этого документа сразу. Страна и владелец не меняются: это определяет, к какому графику относится показатель.</p>
        <div className="two">
          <label>Дата анализа<input type="date" value={bulkEditForm.takenOn} onChange={event => setBulkEditForm(f => ({ ...f, takenOn: event.target.value }))} required /></label>
          <LabInstitutionField id="lab-name-bulk-edit" names={bulkEditLabs} value={bulkEditForm.labName} onChange={labName => setBulkEditForm(form => ({ ...form, labName }))} />
        </div>
        {bulkEditError && <p className="form-error" role="alert">{bulkEditError}</p>}
        <button className="primary" type="submit" disabled={bulkEditBusy}>{bulkEditBusy ? 'Сохраняем…' : 'Изменить у всех показателей'}</button>
      </form>
    </div>}
    {deleteTarget && <div className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-document-title"><section className="form delete-document-dialog">
      <div className="row"><div><p className="eyebrow">Необратимое действие</p><h2 id="delete-document-title">Удалить документ?</h2></div><button type="button" className="ghost" disabled={Boolean(deleting)} onClick={closeDelete} aria-label="Закрыть">×</button></div>
      <p><b>{deleteTarget.document.originalName}</b> будет удалён из защищённого хранилища и исчезнет из архива.</p>
      {deleteConflict && <p className="form-error" role="alert">{deleteConflict}</p>}
      {deleteTarget.readingCount > 0 ? <><p className="form-error" role="alert">С документом связаны подтверждённые показатели: {deleteTarget.readingCount}. Отдельно удалить файл нельзя, чтобы не потерять источник данных.</p><p className="ai-hint">Можно удалить только показатели и оставить файл либо удалить всё осознанно.</p><label>Чтобы удалить документ и показатели, введите «УДАЛИТЬ ВСЁ»<input value={deleteConfirmation} onChange={event => setDeleteConfirmation(event.target.value)} autoComplete="off" /></label><div className="actions delete-document-actions"><button type="button" className="secondary" disabled={Boolean(deleting)} onClick={() => void deleteOnlyResults()}>{deleting === 'results' ? 'Удаляем показатели…' : `Удалить только показатели (${deleteTarget.readingCount})`}</button><button type="button" className="danger-button" disabled={deleteConfirmation !== 'УДАЛИТЬ ВСЁ' || Boolean(deleting)} onClick={() => void deleteDocument('document_and_results')}>{deleting === 'all' ? 'Удаляем всё…' : 'Удалить всё'}</button></div></> : <><p className="ai-hint">К этому файлу не привязаны подтверждённые показатели. Незавершённая обработка, если есть, будет отменена.</p><label>Чтобы подтвердить, введите «УДАЛИТЬ ДОКУМЕНТ»<input value={deleteConfirmation} onChange={event => setDeleteConfirmation(event.target.value)} autoComplete="off" /></label><button type="button" className="danger-button" disabled={deleteConfirmation !== 'УДАЛИТЬ ДОКУМЕНТ' || Boolean(deleting)} onClick={() => void deleteDocument('document_only')}>{deleting === 'document' ? 'Удаляем документ…' : 'Удалить документ'}</button></>}
      <button type="button" className="secondary" disabled={Boolean(deleting)} onClick={closeDelete}>Отмена</button>
    </section></div>}
  </div>;
}

function DocumentTechnicalInfo({ diagnostics, processing, onRetry, onContinue }: {
  diagnostics: DocumentProcessingDiagnostics;
  processing: boolean;
  onRetry: () => void;
  onContinue: () => void;
}) {
  return <div className="stack technical-info" style={{ gap: 6 }}>
    <p><b>Задача:</b> {diagnostics.jobId}</p>
    <p><b>Версия обработки:</b> {diagnostics.processingVersion || 'не указана'}</p>
    <p><b>Пакеты:</b> {diagnostics.totalBatches} · завершено {diagnostics.completedBatches} · в очереди {diagnostics.pendingBatches} · неудачных {diagnostics.failedBatches}</p>
    <p><b>Попыток:</b> {diagnostics.attempts}</p>
    <p><b>Последний диапазон:</b> {diagnostics.lastProcessedRange || 'ещё не обрабатывался'}</p>
    <p><b>Начато:</b> {formatOptionalDocumentDateTime(diagnostics.startedAt)}</p>
    <p><b>Завершено:</b> {formatOptionalDocumentDateTime(diagnostics.finishedAt)}</p>
    {diagnostics.lastError && <p className="form-error" role="alert">{diagnostics.lastError}</p>}
    {diagnostics.canRetry && <button type="button" className="secondary" disabled={processing} onClick={onRetry}>{processing ? 'Обрабатываем…' : 'Повторить обработку'}</button>}
    {diagnostics.canContinue && <button type="button" className="secondary" disabled={processing} onClick={onContinue}>{processing ? 'Обрабатываем…' : 'Продолжить обработку'}</button>}
  </div>;
}

// `value` holds whatever was typed/extracted — a number ("95") or a
// qualitative result ("отрицательно"). Which one it is gets decided at
// save time by whether it parses as a finite number; see submit() below.
interface LabDraftItem { analyte: string; russianName: string; internationalName: string; canonicalCode: string; aliases: string[]; value: string; unit: string; refLow: string; refHigh: string; refText?: string; refFromText?: boolean; panel: string; sourcePage?: number; sourceRow?: number; sourceSection?: string; include: boolean }
const emptyLabDraftItem: LabDraftItem = { analyte: '', russianName: '', internationalName: '', canonicalCode: '', aliases: [], value: '', unit: '', refLow: '', refHigh: '', refText: '', panel: '', include: true };

function draftItemsFromExtraction(extraction: LabExtraction): LabDraftItem[] {
  return extraction.items.map(item => {
    // A one-sided norm (">15 mil/ml", "до 5") usually arrives as text only,
    // because there is no low/high pair to fill. Reading its bound out of that
    // printed text — never out of general knowledge — is what lets the reading
    // get an in/out-of-range badge, a chart band and a trend at all. It is
    // pre-filled into the editable «Норма от/до» fields as a suggestion the
    // user reviews before saving (docs/AI_RULES.md rules 1-2), and the raw
    // printed text is kept alongside it either way.
    const parsedReference = item.referenceLow === undefined && item.referenceHigh === undefined
      ? parseReferenceText(item.referenceText)
      : null;
    const refLow = item.referenceLow ?? parsedReference?.low;
    const refHigh = item.referenceHigh ?? parsedReference?.high;
    return {
      analyte: item.analyte,
      russianName: item.russianName || '',
      internationalName: item.internationalName || '',
      canonicalCode: item.canonicalCode || '',
      aliases: item.aliases || [],
      value: item.value !== undefined ? String(item.value) : (item.valueText || ''),
      unit: item.unit || '',
      refLow: refLow === undefined ? '' : String(refLow),
      refHigh: refHigh === undefined ? '' : String(refHigh),
      refText: item.referenceText || '',
      refFromText: Boolean(parsedReference),
      panel: item.panel || '',
      sourcePage: item.sourcePage,
      sourceRow: item.sourceRow,
      sourceSection: item.sourceSection,
      include: true,
    };
  });
}

function labDraftItemToResult(item: LabDraftItem, index: number, details: { owner: Profile; country: Country; takenOn: string; labName: string; documentId?: string }): LabResult {
  const numeric = isNumericLabValue(item.value);
  return {
    id: `draft-${index}`,
    owner: details.owner,
    analyte: item.analyte.trim(),
    originalName: item.analyte.trim(),
    russianName: item.russianName.trim() || undefined,
    internationalName: item.internationalName.trim() || undefined,
    canonicalCode: item.canonicalCode.trim() || undefined,
    aliases: item.aliases,
    country: details.country,
    labName: details.labName.trim() || undefined,
    value: numeric ? Number(item.value) : undefined,
    valueText: numeric ? undefined : item.value.trim(),
    unit: item.unit.trim() || undefined,
    refLow: item.refLow.trim() ? Number(item.refLow) : undefined,
    refHigh: item.refHigh.trim() ? Number(item.refHigh) : undefined,
    // Kept verbatim even when the bounds above were read out of it: a norm
    // the app could not parse ("0-3% Poor | 4-13% Good") would otherwise be
    // lost, and the printed wording is what a doctor checks against.
    refText: (item.refText || '').trim() || undefined,
    panel: item.panel.trim() || undefined,
    takenOn: details.takenOn,
    documentId: details.documentId,
    sourcePage: item.sourcePage,
    sourceRow: item.sourceRow,
    sourceSection: item.sourceSection,
  };
}

// Same rationale as MEDICINE_DRAFT_KEY below: a lab report extraction can
// take up to ~110s (analyze-lab-report), and if the phone backgrounds long
// enough the tab can get discarded and reload from scratch — without this,
// a finished extraction (which already spent real AI tokens) is lost and
// has to be re-sent to the model. Autosaved to sessionStorage while the
// form is open, restored on mount, cleared on save or explicit close.
const LAB_DRAFT_KEY = 'medai.labDraft.v1';
interface LabDraft {
  document_: string; documentName: string; takenOn: string; labName: string;
  documentId?: string; country: Country | ''; owner: Profile; items: LabDraftItem[]; extract: SuggestStatus; metadataOptions?: DocumentMetadataOptions;
}
const isLabDraftBlank = (d: LabDraft) => !d.document_ && !d.documentId && !d.takenOn && !d.labName && !d.country && !d.items.length;

function loadSavedLabDraft(): LabDraft | null {
  try {
    const raw = sessionStorage.getItem(LAB_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed as LabDraft : null;
  } catch {
    return null;
  }
}

function restoredLabExtractionStatus(extract?: SuggestStatus): SuggestStatus {
  if (extract?.state !== 'loading') return extract || { state: 'idle' };
  // A background task may survive the browser, but a React promise cannot.
  // Never restore a disabled "Распознаём…" button without a live poller.
  return {
    state: 'error',
    note: 'Предыдущая проверка была прервана перезагрузкой страницы. Документ сохранён; нажмите «Распознать анализ (ИИ)», чтобы проверить или продолжить его обработку.',
  };
}

function labProcessingMessage(progress: DocumentProcessingProgress): string {
  const total = Math.max(1, progress.totalBatches);
  const completed = Math.min(Math.max(0, progress.completedBatches), total);
  const page = Math.min(total, completed + 1);
  if (progress.status === 'queued') {
    return completed
      ? `Страница ${completed} из ${total} готова. Подготавливаем страницу ${page} из ${total}…`
      : `Подготавливаем страницу ${page} из ${total}…`;
  }
  return `Распознаём страницу ${page} из ${total}. На плотной странице это может занять до двух минут.`;
}

function metadataConflictFields(options?: DocumentMetadataOptions): Array<'takenOn' | 'labName' | 'countryGuess'> {
  return (['takenOn', 'labName', 'countryGuess'] as const).filter(field => (options?.[field] || []).length > 1);
}

function sourcePagesLabel(candidate: MetadataCandidate): string {
  if (!candidate.sourcePages.length) return 'страница не указана';
  return candidate.sourcePages.length === 1 ? `стр. ${candidate.sourcePages[0]}` : `стр. ${candidate.sourcePages.join(', ')}`;
}

// Trilingual panel/section heading (ru · en · he) for the group above a
// cluster of analyte cards — falls back to the raw printed text for panels
// not in the small static dictionary (src/lib/panelTranslations.ts). The
// Hebrew segment gets its own dir="rtl" since the app has no global bidi
// handling; the language actually printed on the report is underlined.
function PanelHeading({ panel }: { panel: string }) {
  const t = translatePanel(panel);
  if (!t) return <>{panel}</>;
  return <>
    <span className={t.originalLang === 'ru' ? 'panel-original' : undefined}>{t.ru}</span>
    {' · '}
    <span className={t.originalLang === 'en' ? 'panel-original' : undefined}>{t.en}</span>
    {' · '}
    <span dir="rtl" className={t.originalLang === 'he' ? 'panel-original' : undefined}>{t.he}</span>
  </>;
}

function Metrics({ labResults, documents, profile, members, onSaveReport, onUploadDocument, busy, connected }: { labResults: LabResult[]; documents: MedicalDocument[]; profile: Profile; members: FamilyMember[]; onSaveReport: (results: LabResult[], source?: { dataUrl?: string; documentId?: string; owner: Profile; country: Country; takenOn: string; labName?: string }) => Promise<LabReportSaveOutcome>; onUploadDocument: (file: File, owner: Profile) => Promise<string>; busy: boolean; connected: boolean }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewDocumentId = searchParams.get('reviewDocument') || '';
  const openedReviewDocumentId = useRef('');
  const restoredLabDraft = useMemo(loadSavedLabDraft, []);
  const [open, setOpen] = useState(() => Boolean(restoredLabDraft));
  const [document_, setDocument] = useState(() => restoredLabDraft?.document_ || '');
  const [documentName, setDocumentName] = useState(() => restoredLabDraft?.documentName || '');
  const [documentId, setDocumentId] = useState(() => restoredLabDraft?.documentId || '');
  const [takenOn, setTakenOn] = useState(() => restoredLabDraft?.takenOn || '');
  const [labName, setLabName] = useState(() => restoredLabDraft?.labName || '');
  const [country, setCountry] = useState<Country | ''>(() => restoredLabDraft?.country || '');
  const [metadataOptions, setMetadataOptions] = useState<DocumentMetadataOptions>(() => restoredLabDraft?.metadataOptions || {});
  const [owner, setOwner] = useState<Profile>(() => restoredLabDraft?.owner || profile);
  const [items, setItems] = useState<LabDraftItem[]>(() => restoredLabDraft?.items || []);
  const [extract, setExtract] = useState<SuggestStatus>(() => restoredLabExtractionStatus(restoredLabDraft?.extract));
  const [saveError, setSaveError] = useState('');
  const [restoreNotice, setRestoreNotice] = useState(() => Boolean(restoredLabDraft));
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);

  useEffect(() => {
    if (!open) return;
    const draft: LabDraft = { document_, documentName, documentId, takenOn, labName, country, owner, items, extract, metadataOptions };
    if (isLabDraftBlank(draft)) return;
    try {
      sessionStorage.setItem(LAB_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // Quota exceeded (e.g. a large scanned PDF) -- best-effort only, the
      // in-memory state still works for the rest of this session.
    }
  }, [open, document_, documentName, documentId, takenOn, labName, country, owner, items, extract, metadataOptions]);

  const ownResults = useMemo(() => labResults.filter(r => r.owner === profile), [labResults, profile]);
  const laboratories = useMemo(() => [...new Set(ownResults.map(result => result.labName).filter((name): name is string => Boolean(name)))].sort((a, b) => a.localeCompare(b, 'ru')), [ownResults]);
  const [laboratoryFilter, setLaboratoryFilter] = useState('');
  const visibleResults = useMemo(() => laboratoryFilter ? ownResults.filter(result => result.labName === laboratoryFilter) : ownResults, [ownResults, laboratoryFilter]);
  const series = useMemo(() => {
    const map = new Map<string, LabResult[]>();
    for (const r of visibleResults) {
      const key = labSeriesKey(r.analyte, r.country, r.owner, r.canonicalCode, undefined, r.labName);
      const list = map.get(key) || [];
      list.push(r);
      map.set(key, list);
    }
    return [...map.entries()].map(([key, readings]) => ({
      key,
      readings: readings.sort((a, b) => a.takenOn.localeCompare(b.takenOn)),
    })).sort((a, b) => {
      const lastA = a.readings[a.readings.length - 1].takenOn;
      const lastB = b.readings[b.readings.length - 1].takenOn;
      return lastB.localeCompare(lastA);
    });
  }, [visibleResults]);

  const [query, setQuery] = useState('');
  const filteredSeries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return series;
    return series.filter(s => s.readings.some(reading => matchesLabResultSearch(reading, q)));
  }, [series, query]);

  // Comparison mode: picking cards here never merges their data — it only
  // collects series keys to hand to CompareMetrics, which renders each one
  // as its own independent panel (docs/AI_RULES.md: never mix reference
  // ranges/series across countries or labs).
  const [compareMode, setCompareMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  useEffect(() => {
    // A filtered-out card must not remain in the comparison tray: otherwise
    // the next comparison would include a result the user can no longer see.
    setSelectedKeys(current => current.filter(key => filteredSeries.some(seriesItem => seriesItem.key === key)));
  }, [filteredSeries]);
  const toggleCompareMode = () => { setCompareMode(v => !v); setSelectedKeys([]); };
  const onCardClick = (key: string) => {
    if (!compareMode) { navigate(`/metrics/${key}`); return; }
    setSelectedKeys(current => current.includes(key) ? current.filter(k => k !== key) : [...current, key]);
  };

  // Group series by the panel/section the latest reading was printed under
  // (e.g. "Химия крови", "Гематология") so a report with 20-30 analytes
  // reads as a few labeled sections instead of one flat wall of cards.
  // "Без категории" (readings saved before this field existed, or from a
  // document with no named sections) always sorts last.
  const panelGroups = useMemo(() => {
    const map = new Map<string, typeof series>();
    for (const s of filteredSeries) {
      const panelName = s.readings[s.readings.length - 1].panel || 'Без категории';
      const list = map.get(panelName) || [];
      list.push(s);
      map.set(panelName, list);
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === 'Без категории') return 1;
      if (b[0] === 'Без категории') return -1;
      return a[0].localeCompare(b[0], 'ru');
    });
  }, [filteredSeries]);

  // Suggestions for the analyte/panel fields below, scoped to the form's
  // current country+owner (analytes) so a lab report from a different
  // country never suggests a name across the "never mix series" boundary.
  const knownAnalytes = useMemo(() => {
    const set = new Set<string>();
    for (const r of labResults) if (r.owner === owner && r.country === country) set.add(r.analyte);
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [labResults, owner, country]);
  const knownPanels = useMemo(() => {
    const set = new Set<string>();
    for (const r of labResults) if (r.panel) set.add(r.panel);
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [labResults]);
  // Names from either an existing document or an already confirmed result can
  // be selected explicitly. The text supplied by AI remains a draft field.
  const knownLabs = useMemo(() => {
    return knownLabInstitutions([...labResults, ...documents], country);
  }, [labResults, documents, country]);

  const availableDocuments = useMemo(() => documents
    .filter(document => document.owner === owner)
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt)), [documents, owner]);
  const draftResults = useMemo(() => items.map((item, index) => labDraftItemToResult(item, index, {
    owner,
    country: country || 'other',
    takenOn,
    labName,
    documentId: documentId || undefined,
  })), [items, owner, country, takenOn, labName, documentId]);
  const duplicateItemIndexes = useMemo(() => duplicateLabResultIndexes(draftResults, labResults), [draftResults, labResults]);
  const similarManualItemIndexes = useMemo(() => similarManualLabResultIndexes(draftResults, labResults), [draftResults, labResults]);
  const validItemIndexes = useMemo(() => new Set(items.flatMap((item, index) => item.include && item.analyte.trim() && item.value.trim() ? [index] : [])), [items]);
  const duplicateItemCount = useMemo(() => [...validItemIndexes].filter(index => duplicateItemIndexes.has(index)).length, [validItemIndexes, duplicateItemIndexes]);
  const saveableItemCount = validItemIndexes.size - duplicateItemCount;
  const similarManualItemCount = useMemo(() => [...validItemIndexes].filter(index => similarManualItemIndexes.has(index)).length, [validItemIndexes, similarManualItemIndexes]);

  const closeForm = () => { sessionStorage.removeItem(LAB_DRAFT_KEY); setRestoreNotice(false); setShowDocumentPicker(false); setOpen(false); };
  const openForm = () => {
    sessionStorage.removeItem(LAB_DRAFT_KEY);
    setRestoreNotice(false);
    setDocument(''); setDocumentName(''); setDocumentId(''); setTakenOn(''); setLabName(''); setCountry(''); setMetadataOptions({});
    setOwner(profile); setItems([]); setExtract({ state: 'idle' }); setSaveError(''); setShowDocumentPicker(false); setOpen(true);
  };

  const selectExistingDocument = (document: MedicalDocument) => {
    setDocument('');
    setDocumentId(document.id);
    setDocumentName(document.originalName);
    setTakenOn(document.takenOn || '');
    setLabName(document.labName || '');
    setCountry(document.country || '');
    setMetadataOptions({});
    setItems([]);
    setExtract({ state: 'idle' });
    setSaveError('');
    setShowDocumentPicker(false);
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setDocumentName(file.name);
    setExtract({ state: 'idle' });
    setItems([]);
    setMetadataOptions({});
    try {
      if (connected) {
        setDocument('');
        setDocumentId(await onUploadDocument(file, owner));
      } else {
        const dataUrl = file.type === 'application/pdf' ? await fileToDataUrl(file) : await compressImage(file);
        setDocument(dataUrl);
        setDocumentId('');
      }
    } catch (error) {
      setDocumentId('');
      setDocument('');
      setExtract({ state: 'error', note: error instanceof Error ? error.message : 'Не удалось загрузить документ.' });
    }
  };

  const handleExtract = async () => {
    if ((!document_ && !documentId) || !connected) return;
    setExtract({ state: 'loading', note: 'Подготавливаем документ к распознаванию…' });
    try {
      const result = await withWakeLock(() => documentId
        ? processLabDocument(documentId, getAnthropicModel(), progress => {
          setExtract({
            state: 'loading',
            note: labProcessingMessage(progress),
            completedBatches: progress.completedBatches,
            totalBatches: progress.totalBatches,
          });
        })
        : analyzeLabReport(document_, getAnthropicModel()));
      const resultMetadataOptions = result.metadataOptions || {};
      const conflicts = metadataConflictFields(resultMetadataOptions);
      setMetadataOptions(resultMetadataOptions);
      setTakenOn(current => current || result.takenOn || current);
      setLabName(current => current || result.labName || current);
      setCountry(current => current || result.countryGuess || current);
      setItems(current => [...current, ...draftItemsFromExtraction(result)]);
      setExtract({
        state: 'done',
        note: `${result.items.length ? `Распознано показателей: ${result.items.length}` : 'Показатели не распознаны'}${result.note ? ` — ${result.note}` : ''}.${conflicts.length ? ' Найдены разные варианты реквизитов: выберите подходящий перед сохранением.' : ' Проверьте страну и значения перед сохранением.'}`,
      });
    } catch (error) {
      setExtract({ state: 'error', note: error instanceof Error ? error.message : 'Не удалось распознать анализ.' });
    }
  };

  useEffect(() => {
    if (!reviewDocumentId || !connected || openedReviewDocumentId.current === reviewDocumentId) return;
    const document = documents.find(candidate => candidate.id === reviewDocumentId && candidate.owner === profile);
    if (!document) return;

    openedReviewDocumentId.current = reviewDocumentId;
    let cancelled = false;
    sessionStorage.removeItem(LAB_DRAFT_KEY);
    setRestoreNotice(false);
    setOpen(true);
    setDocument('');
    setDocumentId(document.id);
    setDocumentName(document.originalName);
    setTakenOn(document.takenOn || '');
    setLabName(document.labName || '');
    setCountry(document.country || '');
    setMetadataOptions({});
    setOwner(document.owner);
    setItems([]);
    setSaveError('');
    setShowDocumentPicker(false);
    setExtract({ state: 'loading', note: 'Открываем сохранённый результат для проверки…' });

    void loadLabDocumentReview(document.id).then(result => {
      if (cancelled) return;
      const resultMetadataOptions = result.metadataOptions || {};
      const conflicts = metadataConflictFields(resultMetadataOptions);
      setMetadataOptions(resultMetadataOptions);
      setTakenOn(result.takenOn || document.takenOn || '');
      setLabName(result.labName || document.labName || '');
      setCountry(result.countryGuess || document.country || '');
      setItems(draftItemsFromExtraction(result));
      setExtract({
        state: 'done',
        note: `${result.items.length ? `Готово к проверке: ${result.items.length} показателей` : 'Показатели не распознаны'}. ${conflicts.length ? 'Выберите вариант реквизитов перед сохранением.' : 'Проверьте реквизиты и значения перед сохранением.'}`,
      });
    }).catch(error => {
      if (!cancelled) setExtract({ state: 'error', note: error instanceof Error ? error.message : 'Не удалось открыть результат проверки.' });
    }).finally(() => {
      if (cancelled) return;
      const next = new URLSearchParams(searchParams);
      next.delete('reviewDocument');
      setSearchParams(next, { replace: true });
    });

    return () => { cancelled = true; };
  }, [connected, documents, profile, reviewDocumentId, searchParams, setSearchParams]);

  const updateItem = (index: number, patch: Partial<LabDraftItem>) => {
    setItems(current => current.map((item, i) => i === index ? { ...item, ...patch } : item));
  };

  const chooseMetadata = (field: 'takenOn' | 'labName' | 'countryGuess', value: string) => {
    if (field === 'takenOn') setTakenOn(value);
    else if (field === 'labName') setLabName(value);
    else setCountry(value as Country);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!country || !takenOn) { setSaveError('Укажите страну и дату анализа.'); return; }
    const included = items.filter((item, index) => item.include && item.analyte.trim() && item.value.trim() && !duplicateItemIndexes.has(index));
    if (duplicateItemCount && !included.length) {
      setSaveError(`Все ${duplicateItemCount} показателя уже сохранены. Повторные записи не будут добавлены.`);
      return;
    }
    if (!included.length) { setSaveError('Добавьте хотя бы один показатель со значением.'); return; }
    setSaveError('');
    try {
      const results = included.map((item, index) => ({
        ...labDraftItemToResult(item, index, { owner, country, takenOn, labName, documentId: documentId || undefined }),
        id: makeId('lab'),
      }));
      const outcome = await onSaveReport(results, (document_ || documentId) ? {
        dataUrl: document_ || undefined, documentId: documentId || undefined, owner, country, takenOn,
        labName: labName.trim() || undefined,
      } : undefined);
      if (!outcome.savedCount) {
        setSaveError(`Новых показателей не добавлено: ${outcome.skippedDuplicateCount} уже сохранены. Проверьте исходный документ и значения.`);
        return;
      }
      sessionStorage.removeItem(LAB_DRAFT_KEY);
      setRestoreNotice(false);
      setOpen(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Не удалось сохранить анализ');
    }
  };

  return <div className="stack">
    <section className="page-head row"><div><p className="eyebrow">{profileName(profile, members)}</p><h1>Показатели</h1><p>Загружайте анализы по фото или PDF — ИИ распознает значения.</p></div>
      <div className="page-head-actions">
        <button type="button" className={compareMode ? 'primary round' : 'secondary round'} onClick={toggleCompareMode} aria-pressed={compareMode} aria-label="Режим сравнения">⇄</button>
        <button className="primary round" onClick={openForm}>＋</button>
      </div>
    </section>
    {Boolean(ownResults.length) && <div className="row" style={{ gap: 8 }}>
      <input className="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Название, сокращение, лаборатория или раздел" aria-label="Поиск показателей" />
      {laboratories.length > 0 && <select value={laboratoryFilter} onChange={event => setLaboratoryFilter(event.target.value)} aria-label="Фильтр показателей по лаборатории">
        <option value="">Все лаборатории</option>
        {laboratories.map(laboratory => <option value={laboratory} key={laboratory}>{laboratory}</option>)}
      </select>}
    </div>}
    {panelGroups.map(([panelName, group]) => <section className="stack" key={panelName}>
      <p className="eyebrow">{panelName === 'Без категории' ? panelName : <PanelHeading panel={panelName} />}</p>
      <section className="lab-grid">{group.map(({ key, readings }) => {
        const last = readings[readings.length - 1];
        const status = inRange(last.value, last.refLow, last.refHigh);
        const trend = numericTrend(readings);
        const selected = compareMode && selectedKeys.includes(key);
        return <article className={selected ? 'lab-card selected' : 'lab-card'} key={key} onClick={() => onCardClick(key)}>
          <div className="row"><div><h2>{last.originalName || last.analyte}</h2>{last.russianName && last.russianName !== (last.originalName || last.analyte) && <p className="lab-translation">{last.russianName}</p>}</div>
            <div className="row" style={{ gap: 8 }}>
              {compareMode && <input type="checkbox" checked={selected} readOnly aria-label="Выбрать для сравнения" />}
              <span className="badge-count" title={`Замеров: ${readings.length}`} aria-label={`Замеров: ${readings.length}`}>{readings.length}</span>
              <span className="badge">{COUNTRY_LABELS[last.country]}</span>
            </div>
          </div>
          {last.value !== undefined && (last.refLow !== undefined || last.refHigh !== undefined)
            ? <RangeBar value={last.value} unit={last.unit} refLow={last.refLow} refHigh={last.refHigh} status={status} />
            : <p><strong>{last.value !== undefined ? last.value : last.valueText}</strong> {last.value !== undefined ? last.unit : ''}</p>}
          <footer><span className="lab-card-badges"><span className={status === false ? 'badge danger' : status === true ? 'badge ok' : 'badge'}>{status === false ? 'вне нормы' : status === true ? 'в норме' : 'норма не указана'}</span>{trend && <TrendBadge trend={trend} />}</span><span>{last.labName ? `${last.labName} · ` : ''}{new Date(last.takenOn).toLocaleDateString('ru-RU')}</span></footer>
        </article>;
      })}</section>
    </section>)}
    {!ownResults.length && <section className="panel empty"><h2>Пока нет анализов</h2><p>Добавьте первый результат по фото или PDF.</p></section>}
    {Boolean(ownResults.length) && !filteredSeries.length && <section className="panel empty"><h2>Ничего не найдено</h2><p>{laboratoryFilter ? 'В выбранной лаборатории нет подходящих показателей.' : 'Измените запрос поиска.'}</p></section>}
    {compareMode && selectedKeys.length >= 2 && <div className="compare-bar">
      <span>Выбрано: {selectedKeys.length}</span>
      <button type="button" className="primary" onClick={() => navigate(`/metrics/compare?keys=${selectedKeys.map(encodeURIComponent).join(',')}`)}>Сравнить</button>
    </div>}
    {open && <div className="modal" role="dialog" aria-modal="true" aria-labelledby="lab-report-title"><form className="form lab-report-form" onSubmit={submit}>
      <div className="row"><h2 id="lab-report-title">{documentId && items.length ? 'Проверка анализа' : 'Новый анализ'}</h2><button type="button" className="ghost" onClick={closeForm} aria-label="Закрыть">×</button></div>
      {restoreNotice && <p className="ai-hint">Черновик восстановлен после перезагрузки страницы — проверьте поля перед сохранением.</p>}
      <div className="photo-field">
        <span>Фото или PDF результата</span>
        <div className="photo-picker">
          {document_ || documentId ? <span className="photo-placeholder" aria-hidden="true">📄</span> : <span className="photo-placeholder" aria-hidden="true">📷</span>}
          <span className="file-picker-button">
            {document_ || documentId ? 'Изменить файл' : 'Выбрать файл'}
            <input type="file" accept="image/*,application/pdf" onChange={handleFile} />
          </span>
        </div>
        {connected && <button type="button" className="text-button document-picker-open" onClick={() => setShowDocumentPicker(current => !current)}>{showDocumentPicker ? 'Скрыть документы' : 'Выбрать из сохранённых документов'}</button>}
        {documentName && <small className="hint-status">{documentName}</small>}
      </div>
      {showDocumentPicker && connected && <section className="document-picker" aria-label="Сохранённые документы">
        <div className="row"><div><strong>Сохранённые документы</strong><p>Выберите уже загруженный исходный файл — он не будет загружен повторно.</p></div></div>
        {availableDocuments.length ? <div className="document-picker-list">{availableDocuments.map(document => <button type="button" className={document.id === documentId ? 'document-picker-item active' : 'document-picker-item'} onClick={() => selectExistingDocument(document)} key={document.id}>
          <span>📄</span><span><b>{document.originalName}</b><small>{formatOptionalDocumentDate(document.takenOn)} · {DOCUMENT_PROCESSING_STATUS_LABELS[document.processingStatus]}{document.confirmedCount ? ` · сохранено: ${document.confirmedCount}` : ''}</small></span>
        </button>)}</div> : <p className="ai-hint">Для выбранного человека сохранённых документов пока нет.</p>}
      </section>}
      {(document_ || documentId) && connected && <div className="ai-describe">
        <button type="button" className="secondary" disabled={extract.state === 'loading'} onClick={() => void handleExtract()}>{extract.state === 'loading' ? 'Распознаём…' : '🔍 Распознать анализ (ИИ)'}</button>
        {extract.state === 'loading' && <div className="lab-processing-status" role="status" aria-live="polite">
          <div><span className="loading-spinner" aria-hidden="true" /><strong>{extract.note || 'Распознаём документ…'}</strong></div>
          {extract.totalBatches !== undefined && <small>Готово страниц: {extract.completedBatches || 0} из {extract.totalBatches}</small>}
          <progress aria-label="Идёт распознавание документа" />
          <small>Можно оставить страницу открытой или вернуться позже: исходный файл и прогресс сохраняются.</small>
        </div>}
        {extract.state === 'error' && <small className="hint-status hint-status--error">{extract.note}</small>}
        {extract.state === 'done' && <small className="hint-status hint-status--ok">{extract.note}</small>}
      </div>}
      {metadataConflictFields(metadataOptions).length > 0 && <section className="metadata-review" aria-label="Проверка реквизитов документа">
        <p className="form-notice" role="status">В документе найдены разные варианты реквизитов. Приложение не выбрало один автоматически — проверьте и выберите подходящий.</p>
        {metadataConflictFields(metadataOptions).map(field => {
          const label = field === 'takenOn' ? 'Дата анализа' : field === 'labName' ? 'Лаборатория' : 'Страна лаборатории';
          const selected = field === 'takenOn' ? takenOn : field === 'labName' ? labName : country;
          return <section className="metadata-review-field" key={field} aria-label={label}>
            <strong>{label}</strong>
            <div className="metadata-options">{(metadataOptions[field] || []).map(candidate => <button key={`${field}-${candidate.value}`} type="button" className={selected === candidate.value ? 'metadata-choice active' : 'metadata-choice'} aria-pressed={selected === candidate.value} onClick={() => chooseMetadata(field, candidate.value)}>{field === 'countryGuess' ? COUNTRY_LABELS[candidate.value as Country] : candidate.value} <small>{sourcePagesLabel(candidate)}</small></button>)}</div>
          </section>;
        })}
      </section>}
      {(document_ || documentId) && !connected && <p className="ai-hint">Распознавание анализа доступно после входа в облачную аптечку (см. «Ещё»).</p>}
      <div className="two">
        <label>Дата анализа<input type="date" value={takenOn} onChange={e => setTakenOn(e.target.value)} required /></label>
        <label>Страна лаборатории<select value={country} onChange={e => setCountry(e.target.value as Country)} required>
          <option value="" disabled>Выберите…</option>
          <option value="russia">Россия</option>
          <option value="israel">Израиль</option>
          <option value="other">Другое</option>
        </select></label>
      </div>
      <div className="two">
        <LabInstitutionField id="lab-name" names={knownLabs} value={labName} onChange={setLabName} />
        <label>Кому<select value={owner} onChange={e => setOwner(e.target.value as Profile)}>{members.map(member => <option key={member.userId} value={member.userId}>{member.displayName}</option>)}</select></label>
      </div>
      <datalist id="known-analytes">{knownAnalytes.map(a => <option key={a} value={a} />)}</datalist>
      <datalist id="known-panels">{knownPanels.map(p => <option key={p} value={p} />)}</datalist>
      <div className="stack">
        <p className="eyebrow">Показатели</p>
        {duplicateItemCount > 0 && <p className="form-notice" role="status">{duplicateItemCount === 1 ? 'Одна строка этого документа уже сохранена и не будет добавлена повторно.' : `${duplicateItemCount} строки этого документа уже сохранены и не будут добавлены повторно.`}</p>}
        {similarManualItemCount > 0 && <p className="ai-hint" role="status">{similarManualItemCount === 1 ? 'Есть похожая ручная запись.' : `Есть ${similarManualItemCount} похожих ручных записи.`} Это только предупреждение: одинаковые названия и значения можно сохранить, если это разные измерения.</p>}
        {items.map((item, index) => <div className="lab-item" key={index}>
          <div className="lab-item-row">
            <input value={item.analyte} onChange={e => updateItem(index, { analyte: e.target.value })} placeholder="Показатель" list="known-analytes" required />
            <input value={item.value} onChange={e => updateItem(index, { value: e.target.value })} placeholder="Значение (число или текст)" required />
            <input value={item.unit} onChange={e => updateItem(index, { unit: e.target.value })} placeholder="Ед." />
            <input value={item.refLow} onChange={e => updateItem(index, { refLow: e.target.value, refFromText: false })} placeholder="Норма от" inputMode="decimal" />
            <input value={item.refHigh} onChange={e => updateItem(index, { refHigh: e.target.value, refFromText: false })} placeholder="Норма до" inputMode="decimal" />
            <input value={item.panel} onChange={e => updateItem(index, { panel: e.target.value })} placeholder="Панель" list="known-panels" />
            <button type="button" className="ghost danger-text" onClick={() => setItems(current => current.filter((_, i) => i !== index))}>×</button>
          </div>
          {item.refText && <small className="lab-item-reference">Норма на бланке: <b>{item.refText}</b>{item.refFromText ? ' — подставлена в поля «Норма от/до», проверьте' : !item.refLow && !item.refHigh ? ' — в числа не переводится, сохраним текстом' : ''}</small>}
        </div>)}
        <button type="button" className="secondary" onClick={() => setItems(current => [...current, { ...emptyLabDraftItem }])}>+ Добавить показатель вручную</button>
      </div>
      {saveError && <p className="form-error" role="alert">{saveError}</p>}
      <button className="primary" type="submit" disabled={busy || (validItemIndexes.size > 0 && saveableItemCount === 0)}>{busy ? 'Сохраняем…' : saveableItemCount ? `Сохранить новые показатели (${saveableItemCount})` : duplicateItemCount ? 'Все показатели уже сохранены' : 'Сохранить'}</button>
    </form></div>}
  </div>;
}

// A quick single-reading add/edit form scoped to one already-known series —
// analyte/country/owner are fixed by the series itself (see submitReading),
// so unlike the "Новый анализ" form this never risks starting a second,
// disconnected series from a typo.
interface ReadingDraft { id?: string; takenOn: string; value: string; unit: string; refLow: string; refHigh: string; panel: string; labName: string }

function MetricDetail({ labResults, members, onSave, onUpdate, onRemove, onAttachDocument, busy, connected, householdId, userId }: { labResults: LabResult[]; members: FamilyMember[]; onSave: (r: LabResult) => Promise<void>; onUpdate: (r: LabResult) => Promise<void>; onRemove: (id: string) => Promise<void>; onAttachDocument: (reading: LabResult, file: File) => Promise<void>; busy: boolean; connected: boolean; householdId?: string; userId?: string }) {
  const navigate = useNavigate();
  const [attachingId, setAttachingId] = useState('');
  const [attachError, setAttachError] = useState('');
  const attachReading = async (reading: LabResult, file: File) => {
    setAttachingId(reading.id);
    setAttachError('');
    try {
      await onAttachDocument(reading, file);
    } catch (error) {
      setAttachError(errorText(error));
    } finally {
      setAttachingId('');
    }
  };
  const { key } = useParams<{ key: string }>();
  const [ai, setAi] = useState<SuggestStatus>({ state: 'idle' });
  const [cached, setCached] = useState<CachedLabRecommendation | null>(null);
  const [readingForm, setReadingForm] = useState<ReadingDraft | null>(null);
  const [readingError, setReadingError] = useState('');

  const series = useMemo(() => seriesReadingsForKey(labResults, key), [labResults, key]);
  const parsed = series?.parsed || null;
  const readings = series?.readings || [];
  // The editable manual-reading form uses the same explicit picker as the
  // recognition form, without treating differently written aliases as equal.
  const knownLabs = useMemo(() => {
    if (!parsed) return [];
    return knownLabInstitutions(labResults, parsed.country);
  }, [labResults, parsed]);
  const stats = computeSeriesStats(readings);
  const { numericReadings } = stats;
  const lastNumericTakenOn = numericReadings.length ? numericReadings[numericReadings.length - 1].takenOn : undefined;

  // Load the last saved recommendation for this series once (not per
  // reading/render) so revisiting the page doesn't spend tokens again — the
  // user only pays for a fresh AI call when they explicitly click
  // "Обновить". Silent on failure: the cache is a convenience, not
  // required for the button to work. Placed above the "not found" early
  // return below so it stays an unconditional hook call (rules of hooks).
  useEffect(() => {
    if (!connected || !householdId || !parsed || !numericReadings.length) { setCached(null); return; }
    let cancelled = false;
    loadCachedRecommendation(householdId, parsed.owner, parsed.country, parsed.analyte, parsed.unit)
      .then(result => { if (!cancelled) setCached(result); })
      .catch(() => { if (!cancelled) setCached(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, householdId, parsed?.owner, parsed?.country, parsed?.analyte, parsed?.unit]);

  if (!parsed || !readings.length) return <div className="stack"><section className="panel empty"><h2>Показатель не найден</h2><button className="secondary" onClick={() => navigate('/metrics')}>Назад к показателям</button></section></div>;

  const lastReading = readings[readings.length - 1];
  const openAddReading = () => {
    setReadingError('');
    setReadingForm({
      takenOn: '', value: '', unit: lastReading.unit || '',
      refLow: lastReading.refLow !== undefined ? String(lastReading.refLow) : '',
      refHigh: lastReading.refHigh !== undefined ? String(lastReading.refHigh) : '',
      panel: lastReading.panel || '', labName: lastReading.labName || '',
    });
  };
  const openEditReading = (r: LabResult) => {
    setReadingError('');
    setReadingForm({
      id: r.id, takenOn: r.takenOn, value: r.value !== undefined ? String(r.value) : (r.valueText || ''),
      unit: r.unit || '', refLow: r.refLow !== undefined ? String(r.refLow) : '', refHigh: r.refHigh !== undefined ? String(r.refHigh) : '',
      panel: r.panel || '', labName: r.labName || '',
    });
  };
  const submitReading = async (event: FormEvent) => {
    event.preventDefault();
    if (!readingForm) return;
    if (!readingForm.takenOn || !readingForm.value.trim()) { setReadingError('Укажите дату и значение.'); return; }
    setReadingError('');
    const numeric = isNumericLabValue(readingForm.value);
    const original = readingForm.id ? readings.find(r => r.id === readingForm.id) : undefined;
    const payload: LabResult = {
      ...original,
      id: readingForm.id || makeId('lab'),
      owner: parsed.owner, analyte: parsed.analyte, country: parsed.country,
      labName: readingForm.labName.trim() || undefined,
      value: numeric ? Number(readingForm.value) : undefined,
      valueText: numeric ? undefined : readingForm.value.trim(),
      unit: readingForm.unit.trim() || undefined,
      refLow: readingForm.refLow.trim() ? Number(readingForm.refLow) : undefined,
      refHigh: readingForm.refHigh.trim() ? Number(readingForm.refHigh) : undefined,
      panel: readingForm.panel.trim() || undefined,
      takenOn: readingForm.takenOn,
    };
    try {
      if (readingForm.id) await onUpdate(payload); else await onSave(payload);
      setReadingForm(null);
    } catch (error) {
      setReadingError(error instanceof Error ? error.message : 'Не удалось сохранить замер.');
    }
  };

  const isStale = Boolean(cached) && cached !== null && (cached.readingCount !== numericReadings.length || cached.lastTakenOn !== lastNumericTakenOn);

  const handleRecommend = async () => {
    setAi({ state: 'loading' });
    try {
      const summary = await withWakeLock(() => recommendLabTrend(parsed.analyte, parsed.country, numericReadings.map(r => ({
        takenOn: r.takenOn, value: r.value, unit: r.unit, referenceLow: r.refLow, referenceHigh: r.refHigh,
      })), getAnthropicModel()));
      setAi({ state: 'idle' });
      const readingCount = numericReadings.length;
      const generatedAt = new Date().toISOString();
      setCached({ summary, readingCount, lastTakenOn: lastNumericTakenOn || '', generatedAt });
      if (householdId && userId) {
        void saveCachedRecommendation(householdId, userId, {
          owner: parsed.owner, country: parsed.country, analyte: parsed.analyte, unit: parsed.unit,
          summary, readingCount, lastTakenOn: lastNumericTakenOn || '',
        }).catch(() => {
          // Cache save failing shouldn't surface as an error — the user
          // already has their answer on screen, they'll just be asked
          // again (and it'll save again) next visit.
        });
      }
    } catch (error) {
      setAi({ state: 'error', note: error instanceof Error ? error.message : 'Не удалось получить рекомендацию.' });
    }
  };

  return <div className="stack">
    <button className="text-button" onClick={() => navigate('/metrics')}>← Назад к показателям</button>
    <section className="page-head"><p className="eyebrow">{COUNTRY_LABELS[parsed.country]} · {profileName(parsed.owner, members)}</p><h1>{parsed.analyte}</h1></section>
    <SeriesPanel stats={stats} />
    <section className="panel">
      <h2>Рекомендация по ИИ</h2>
      {!numericReadings.length ? <p className="ai-hint">Доступно только для показателей с числовым значением.</p>
        : connected
        ? <div className="ai-describe">
            <button type="button" className="secondary" disabled={ai.state === 'loading'} onClick={() => void handleRecommend()}>{ai.state === 'loading' ? 'Спрашиваем ИИ…' : cached ? '🔄 Обновить рекомендацию' : '✨ Получить рекомендацию'}</button>
            {ai.state === 'error' && <small className="hint-status hint-status--error">{ai.note}</small>}
            {cached && ai.state !== 'loading' && <p className="ai-hint">
              {cached.summary} — это не диагноз и не медицинский совет, а ознакомительная информация; при отклонениях или перед изменением образа жизни обратитесь к врачу.
              {isStale && <><br /><small>С момента этой рекомендации добавились новые данные — нажмите «Обновить», если хотите актуальный ответ.</small></>}
            </p>}
          </div>
        : <p className="ai-hint">Доступно после входа в облачную аптечку (см. «Ещё»).</p>}
    </section>
    <section className="panel">
      <div className="row"><h2>Все замеры</h2><button type="button" className="secondary" onClick={openAddReading}>+ Добавить показатель</button></div>
      {attachError && <p className="form-error" role="alert">{attachError}</p>}
      <div className="stack">{readings.slice().reverse().map(r => {
        const status = inRange(r.value, r.refLow, r.refHigh);
        const belowLow = r.value !== undefined && r.refLow !== undefined && r.value < r.refLow;
        const aboveHigh = r.value !== undefined && r.refHigh !== undefined && r.value > r.refHigh;
        const statusLabel = belowLow ? 'ниже нормы' : aboveHigh ? 'выше нормы' : status === true ? 'в норме' : 'норма не указана';
        const hasRange = r.value !== undefined && (r.refLow !== undefined || r.refHigh !== undefined);
        const hasFile = Boolean(r.documentId || r.sourceDocument);
        return <div className="reading-row" key={r.id}>
          <div className="row">
            <small className="reading-row-date">{new Date(r.takenOn).toLocaleDateString('ru-RU')}{r.labName ? ` · ${r.labName}` : ''}</small>
            <div className="row" style={{ gap: 8 }}>
              {r.value !== undefined && <span className={status === false ? 'badge danger' : status === true ? 'badge ok' : 'badge'}>{statusLabel}</span>}
              {!hasFile && <label className="file-picker-button file-picker-button--pill">{attachingId === r.id ? 'Прикрепляем…' : '+ Прикрепить файл'}<input type="file" accept="application/pdf,image/*" disabled={busy || Boolean(attachingId)} onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void attachReading(r, file); }} /></label>}
              <OverflowMenu label="Действия с замером" items={[
                ...(hasFile ? [
                  { label: '📄 Открыть оригинал', onClick: () => void openLabSourceDocument(r.documentId, r.sourceDocument).catch(error => alert(errorText(error))) },
                  { label: '🗂 Показать в документах', onClick: () => r.documentId && navigate(`/documents?tab=analyses&highlight=${encodeURIComponent(r.documentId)}`) },
                ] : []),
                { label: '✎ Редактировать', onClick: () => openEditReading(r) },
                { label: '✕ Удалить', onClick: () => { if (confirm('Удалить этот замер?')) void onRemove(r.id); }, danger: true },
              ]} />
            </div>
          </div>
          {hasRange
            ? <MiniRangeBar value={r.value as number} unit={r.unit} refLow={r.refLow} refHigh={r.refHigh} status={status} />
            : <p><strong>{r.value !== undefined ? r.value : r.valueText}</strong> {r.value !== undefined ? r.unit : ''}</p>}
        </div>;
      })}</div>
    </section>
    {readingForm && <div className="modal" role="dialog" aria-modal="true"><form className="form" onSubmit={submitReading}>
      <div className="row"><h2>{readingForm.id ? 'Изменить замер' : 'Новый замер'}</h2><button type="button" className="ghost" onClick={() => setReadingForm(null)}>×</button></div>
      <div className="two">
        <label>Дата<input type="date" value={readingForm.takenOn} onChange={e => setReadingForm(f => f && { ...f, takenOn: e.target.value })} required /></label>
        <label>Значение<input value={readingForm.value} onChange={e => setReadingForm(f => f && { ...f, value: e.target.value })} placeholder="Число или текст" required /></label>
      </div>
      <div className="two">
        <label>Единица<input value={readingForm.unit} onChange={e => setReadingForm(f => f && { ...f, unit: e.target.value })} /></label>
        <LabInstitutionField id="lab-name-reading" names={knownLabs} value={readingForm.labName} onChange={labName => setReadingForm(form => form && ({ ...form, labName }))} />
      </div>
      <div className="two">
        <label>Норма от<input value={readingForm.refLow} onChange={e => setReadingForm(f => f && { ...f, refLow: e.target.value })} inputMode="decimal" /></label>
        <label>Норма до<input value={readingForm.refHigh} onChange={e => setReadingForm(f => f && { ...f, refHigh: e.target.value })} inputMode="decimal" /></label>
      </div>
      <label>Панель<input value={readingForm.panel} onChange={e => setReadingForm(f => f && { ...f, panel: e.target.value })} /></label>
      {readingError && <p className="form-error" role="alert">{readingError}</p>}
      <button className="primary" type="submit" disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
    </form></div>}
  </div>;
}

type NumericLabResult = LabResult & { value: number };

// Shared by MetricDetail and CompareMetrics so a series always renders the
// same chart/range-bar/stats — whether it's shown alone on the metric page
// or side-by-side with other independent series on the comparison screen.
function computeSeriesStats(readings: LabResult[]) {
  const numericReadings = readings.filter((r): r is NumericLabResult => r.value !== undefined);
  const textReadings = readings.filter(r => r.value === undefined && Boolean(r.valueText?.trim()));
  const min = numericReadings.length ? Math.min(...numericReadings.map(r => r.value)) : undefined;
  const max = numericReadings.length ? Math.max(...numericReadings.map(r => r.value)) : undefined;
  const numericLast = numericReadings[numericReadings.length - 1];
  // "Норма" here is the reference range printed on the *latest* document —
  // not a general-knowledge range (see docs/AI_RULES.md) and not merged
  // across readings, since a range can change between reports.
  const lastStatus = numericLast ? inRange(numericLast.value, numericLast.refLow, numericLast.refHigh) : null;
  // The compact "≥ 15" / "7–8.7" form when the norm is numeric, the report's
  // own wording when it isn't (a Kruger "0-3% Poor | 4-13% Good" line has no
  // numbers this app is willing to guess at).
  const normText = referenceCondition(numericLast?.refLow, numericLast?.refHigh) || numericLast?.refText || '—';
  return {
    numericReadings,
    min,
    max,
    numericLast,
    lastStatus,
    normText,
    normTitle: numericLast?.refText,
    // A qualitative reading can still carry a printed norm (a spermogram
    // prints "(0 – 1) mil/ml" next to a "1-2" leucocyte count). It can't be
    // compared to a text value, but it belongs on screen next to it.
    qualitativeNorm: (() => {
      const last = textReadings[textReadings.length - 1];
      return last ? referenceCondition(last.refLow, last.refHigh) || last.refText : undefined;
    })(),
    trend: numericTrend(readings),
    // Non-numeric readings still have a story over time — most of a
    // spermogram is "Normal" / "+" / "++" — so they get their own timeline
    // instead of an empty chart.
    qualitative: qualitativeTrend(readings),
  };
}
type SeriesStats = ReturnType<typeof computeSeriesStats>;

// A numeric series gets the chart, the range bar and the min/max/last/norm
// tiles; a qualitative one (all-"Normal" liquefaction, "+"/"++" motility, an
// "отрицательно" dipstick) gets a dated timeline instead of an empty graph.
// Either kind gets the trend summary, as soon as there are two readings.
function SeriesPanel({ stats }: { stats: SeriesStats }) {
  const { numericReadings, min, max, numericLast, lastStatus, normText, normTitle, qualitativeNorm, trend, qualitative } = stats;
  if (!numericReadings.length && !qualitative) return null;
  const unit = numericLast?.unit;
  return <>
    {numericReadings.length > 0 && <>
      <section className="panel stack">
        <LabChart readings={numericReadings} />
        <RangeBar value={numericLast.value} unit={unit} refLow={numericLast.refLow} refHigh={numericLast.refHigh} status={lastStatus} />
      </section>
      <section className="stats">
        <article><strong>{min}</strong><span>минимум</span></article>
        <article><strong>{max}</strong><span>максимум</span></article>
        <article className={lastStatus === false ? 'danger' : undefined}><strong>{numericLast.value}</strong><span>последнее{unit ? `, ${unit}` : ''}</span></article>
        <article><strong title={normTitle}>{normText}</strong><span>норма{unit ? `, ${unit}` : ''}</span></article>
      </section>
    </>}
    {qualitative && <section className="panel stack">
      <div className="row"><h2>Качественный показатель</h2>{qualitativeNorm && <span className="badge">норма на бланке: {qualitativeNorm}</span>}</div>
      <QualitativeTimeline trend={qualitative} />
      {qualitativeNorm && <p className="ai-hint">Значение записано текстом — приложение не сравнивает его с напечатанной нормой автоматически.</p>}
    </section>}
    <TrendPanel trend={trend} qualitative={qualitative} unit={unit} readingCount={numericReadings.length + (qualitative?.points.length || 0)} />
  </>;
}

// Direction and size of the last change, plus how it moved relative to the
// range printed on the report. Deliberately says nothing about whether the
// direction is good news — that is a clinical judgement this app does not
// make (docs/AI_RULES.md rule 5).
function TrendPanel({ trend, qualitative, unit, readingCount }: { trend: NumericTrend | null; qualitative: QualitativeTrend | null; unit?: string; readingCount: number }) {
  return <section className="panel stack trend-panel">
    <div className="row"><h2>Динамика</h2>{trend && <TrendBadge trend={trend} />}</div>
    {trend
      ? <p>{trendSentence(trend, unit)}</p>
      : qualitative
        ? <p>{qualitativeSentence(qualitative)}</p>
        : <p>{readingCount > 1 ? 'Для динамики нужны хотя бы два числовых замера этого показателя.' : 'Пока только один замер — динамика появится после второго.'}</p>}
    {trend && trend.readingCount > 2 && <p className="ai-hint">Всего числовых замеров: {trend.readingCount}, первый — {formatIsoDate(trend.first.takenOn)} ({formatLabNumber(trend.first.value)}{unit ? ` ${unit}` : ''}).</p>}
  </section>;
}

// Arrow + magnitude, colored only by whether the reading moved toward or away
// from the printed range (never by the direction itself). The arrow glyph and
// the sign carry the same information as the color, so the chip stays
// readable without color vision.
function TrendBadge({ trend }: { trend: NumericTrend }) {
  const tone = trend.rangeShift === 'entered' || trend.rangeShift === 'closer' ? 'ok'
    : trend.rangeShift === 'left' || trend.rangeShift === 'further' ? 'danger'
      : '';
  return <span className={`badge trend-badge ${tone}`}>{formatTrendDelta(trend)}</span>;
}

// Qualitative readings over time. When every value sits on the same printed
// ladder ("+" … "++++", Normal/Abnormal, Negative/Positive) it is drawn as
// steps between named levels; otherwise it stays a list of dated values, with
// no invented ordering between them.
function QualitativeTimeline({ trend }: { trend: QualitativeTrend }) {
  if (!trend.scale || trend.points.length < 2) {
    return <ol className="qualitative-list">
      {trend.points.map(point => <li key={point.id}>
        <span>{formatIsoDate(point.takenOn)}</span><strong>{point.text}</strong>
      </li>)}
    </ol>;
  }

  const width = 640;
  const height = 180;
  const leftX = 96;
  const rightX = width - 34;
  const padY = 30;
  const labels = trend.scale.labels;
  const rowHeight = (height - padY * 2) / Math.max(1, labels.length - 1);
  const yScale = (level: number) => height - padY - level * rowHeight;
  const xStep = trend.points.length > 1 ? (rightX - leftX) / (trend.points.length - 1) : 0;
  const xScale = (index: number) => leftX + index * xStep;
  const linePoints = trend.points.map((point, index) => `${xScale(index)},${yScale(point.level || 0)}`).join(' ');

  return <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Изменение качественного показателя по датам">
    {labels.map((label, level) => <g key={label}>
      <line x1={leftX} x2={rightX} y1={yScale(level)} y2={yScale(level)} stroke="var(--border)" strokeWidth={1} />
      <text x={0} y={yScale(level)} dy={5} fontSize={15} fill="var(--muted)">{label}</text>
    </g>)}
    <polyline points={linePoints} fill="none" stroke="var(--brand)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    {trend.points.map((point, index) => <g key={point.id}>
      <circle cx={xScale(index)} cy={yScale(point.level || 0)} r={5} fill="var(--brand)" stroke="var(--surface)" strokeWidth={2}>
        <title>{`${formatIsoDate(point.takenOn)}: ${point.text}`}</title>
      </circle>
      <text x={xScale(index)} y={height - 6} fontSize={13} fill="var(--muted)" textAnchor="middle">{formatIsoDate(point.takenOn).slice(0, 5)}</text>
    </g>)}
  </svg>;
}

// The comparison screen for two or more series picked in Metrics' compare
// mode. Each key becomes its own independent panel — same chart/RangeBar/
// stats as the single-metric page — never merged into one chart or axis,
// since reference ranges and units differ by country/lab (docs/AI_RULES.md).
function CompareMetrics({ labResults }: { labResults: LabResult[] }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const keys = (searchParams.get('keys') || '').split(',').filter(Boolean).map(decodeURIComponent);
  const sides = keys
    .map(key => seriesReadingsForKey(labResults, key))
    .filter((s): s is NonNullable<typeof s> => Boolean(s?.readings.length));

  if (sides.length < 2) {
    return <div className="stack">
      <button className="text-button" onClick={() => navigate('/metrics')}>← Назад к показателям</button>
      <section className="panel empty"><h2>Нечего сравнивать</h2><p>Выберите минимум два показателя на странице «Показатели».</p></section>
    </div>;
  }

  return <div className="stack">
    <button className="text-button" onClick={() => navigate('/metrics')}>← Назад к показателям</button>
    {sides.map((side, i) => {
      const last = side.readings[side.readings.length - 1];
      const stats = computeSeriesStats(side.readings);
      return <section className="panel stack" key={i}>
        <div className="row">
          <div><h2>{last.originalName || last.analyte}</h2>{last.russianName && last.russianName !== (last.originalName || last.analyte) && <p className="lab-translation">{last.russianName}</p>}</div>
          <span className="badge">{COUNTRY_LABELS[side.parsed.country]}</span>
        </div>
        {last.labName && <p className="eyebrow">{last.labName}</p>}
        <SeriesPanel stats={stats} />
      </section>;
    })}
  </div>;
}

// Position a reading between its printed reference bounds — the compact
// "position in range" widget lab reports themselves print inline (dotted
// line, marker, bounds at either end). Color is the only channel that says
// in/out of range on the marker itself, which the dataviz palette validator
// flags as a CVD-risk pair (ok green vs danger red, ΔE in the 6-8 floor
// band) — legal only with a secondary encoding, provided here by the
// value's own text turning red (danger-text) alongside the marker.
// A one-sided norm (">15 mil/ml" on a spermogram, "до 5" on a Russian form)
// has no second bound to stretch the track between, so `rangeGeometry` builds
// a window around the value and the printed threshold and reports which part
// of it is the norm — the track is then neutral with only that part shaded,
// and the threshold labels itself where it actually sits.
function RangeBar({ value, unit, refLow, refHigh, status }: { value: number; unit?: string; refLow?: number; refHigh?: number; status: boolean | null }) {
  const geometry = rangeGeometry(value, refLow, refHigh);
  if (!geometry) return null;
  const oneSided = geometry.kind !== 'range';
  return <div className="range-bar">
    <div className="range-bar-head">
      <span>Норма{oneSided ? ` ${geometry.thresholdLabel}` : ''}</span>
      <span className={status === false ? 'range-bar-value danger-text' : 'range-bar-value'}>{value}{unit ? ` ${unit}` : ''}</span>
    </div>
    <div className={oneSided ? 'range-bar-track range-bar-track--windowed' : 'range-bar-track'}>
      {oneSided && <span className="range-bar-band" style={{ left: `${geometry.bandStartPercent}%`, width: `${geometry.bandEndPercent - geometry.bandStartPercent}%` }} />}
      <span className="range-bar-marker" style={{ left: `${geometry.markerPercent}%`, background: status === false ? 'var(--danger)' : 'var(--ok)' }} />
    </div>
    <div className={oneSided ? 'range-bar-ends range-bar-ends--threshold' : 'range-bar-ends'}>
      {oneSided
        ? <span className="range-bar-threshold" style={{ left: `${geometry.thresholdPercent}%` }}>{geometry.thresholdLabel}</span>
        : <><span>{geometry.startLabel}</span><span>{geometry.endLabel}</span></>}
    </div>
  </div>;
}

// Compact per-reading variant of RangeBar for the "Все замеры" history list —
// date/status/actions already sit in their own header row above this, so
// there's no room (or need) for a repeated "Норма" label; the value itself
// sits centered under the track, flanked by the printed low/high bounds.
function MiniRangeBar({ value, unit, refLow, refHigh, status }: { value: number; unit?: string; refLow?: number; refHigh?: number; status: boolean | null }) {
  const geometry = rangeGeometry(value, refLow, refHigh);
  if (!geometry) return null;
  const oneSided = geometry.kind !== 'range';
  return <div className="mini-range">
    <div className={oneSided ? 'mini-range-track mini-range-track--windowed' : 'mini-range-track'}>
      {oneSided && <span className="mini-range-band" style={{ left: `${geometry.bandStartPercent}%`, width: `${geometry.bandEndPercent - geometry.bandStartPercent}%` }} />}
      <span className="mini-range-marker" style={{ left: `${geometry.markerPercent}%`, background: status === false ? 'var(--danger)' : 'var(--ok)' }} />
    </div>
    <div className="mini-range-ends">
      {/* A floor keeps its label on the left, a ceiling on the right — the
          side the out-of-range half of the track is on. */}
      <span>{geometry.kind === 'min' ? geometry.thresholdLabel : geometry.startLabel || ''}</span>
      <strong className={status === false ? 'danger-text' : undefined}>{value}{unit ? ` ${unit}` : ''}</strong>
      <span>{geometry.kind === 'max' ? geometry.thresholdLabel : geometry.endLabel || ''}</span>
    </div>
  </div>;
}

// Clean-ish tick labels for the y-axis — not full nice-number rounding
// (no charting library here), just enough precision to read as a real
// number rather than a float artifact: whole values for a wide span,
// one decimal for a narrow one (most lab units, e.g. hemoglobin g/dl).
function formatTick(value: number, decimals: number): string {
  return value.toFixed(decimals).replace(/\.0$/, '');
}

function LabChart({ readings }: { readings: NumericLabResult[] }) {
  const width = 640;
  const height = 220;
  // Asymmetric: the y-axis labels live in the left margin only (leftX just
  // needs to fit "18.5"-width text), while the right margin has to fit half
  // of the last point's centered date label ("15.11") without clipping it
  // against the svg edge.
  const leftX = 40;
  const rightX = width - 34;
  // Every point gets a value label now (not just out-of-range ones — each
  // point is a specific lab draw, its number should be readable without a
  // hover), so top/bottom need more headroom than just the axis needed. The
  // svg is drawn at viewBox width 640 but rendered around ~380px on a phone
  // (width="100%" of the panel) — a ~0.6x scale-down, so font sizes here
  // need to run noticeably larger than they'd look right at 1:1 to still
  // read comfortably on an actual screen.
  const padY = 46;
  const values = readings.map(r => r.value);
  const last = readings[readings.length - 1];
  const rangeLow = last.refLow;
  const rangeHigh = last.refHigh;
  const bounds = [...values, ...(rangeLow !== undefined ? [rangeLow] : []), ...(rangeHigh !== undefined ? [rangeHigh] : [])];
  const dataMin = Math.min(...bounds);
  const dataMax = Math.max(...bounds);
  const span = dataMax - dataMin || 1;
  const yMin = dataMin - span * 0.1;
  const yMax = dataMax + span * 0.1;
  const yScale = (value: number) => height - padY - ((value - yMin) / (yMax - yMin || 1)) * (height - padY * 2);
  const xStep = readings.length > 1 ? (rightX - leftX) / (readings.length - 1) : 0;
  const xScale = (index: number) => leftX + index * xStep;
  const tickDecimals = yMax - yMin < 10 ? 1 : 0;
  const yTicks = [yMax, (yMax + yMin) / 2, yMin];

  const linePoints = readings.map((r, i) => `${xScale(i)},${yScale(r.value)}`).join(' ');

  return <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label={`График показателя ${last.analyte}`}>
    {yTicks.map((t, i) => <g key={i}>
      <line x1={leftX} x2={rightX} y1={yScale(t)} y2={yScale(t)} stroke="var(--border)" strokeWidth={1} />
      <text x={0} y={yScale(t)} dy={4} fontSize={14} fill="var(--muted)">{formatTick(t, tickDecimals)}</text>
    </g>)}
    {/* The normal band is drawn for a one-sided norm too — it simply runs to
        the top or bottom edge of the plot, because ">15" means everything
        above 15 is normal. Its open edge carries a dashed threshold line so
        the band never reads as an invented second bound. */}
    {(rangeLow !== undefined || rangeHigh !== undefined) && <g>
      <rect
        x={leftX}
        y={rangeHigh !== undefined ? yScale(rangeHigh) : yScale(yMax)}
        width={rightX - leftX}
        height={Math.max(0, (rangeLow !== undefined ? yScale(rangeLow) : yScale(yMin)) - (rangeHigh !== undefined ? yScale(rangeHigh) : yScale(yMax)))}
        fill="var(--brand-soft)"
        opacity={0.6}
      />
      {[rangeLow, rangeHigh].map((bound, i) => bound === undefined ? null : <line
        key={i} x1={leftX} x2={rightX} y1={yScale(bound)} y2={yScale(bound)}
        stroke="var(--brand)" strokeWidth={1} strokeDasharray="5 4" opacity={0.75}
      />)}
    </g>}
    <polyline points={linePoints} fill="none" stroke="var(--brand)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    {readings.map((r, i) => {
      const status = inRange(r.value, r.refLow, r.refHigh);
      const fill = status === false ? 'var(--danger)' : 'var(--ok)';
      // Each point is a specific analysis event — its value is labeled
      // directly, not just on hover. Out-of-range keeps the danger color
      // (also a secondary encoding alongside the red dot, see LabChart's
      // comment above); in-range uses ink (currentColor), not the --ok
      // green, per "text never wears the data color" — the dot already
      // carries that identity.
      const labelColor = status === false ? 'var(--danger)' : 'currentColor';
      return <g key={r.id}>
        <circle cx={xScale(i)} cy={yScale(r.value)} r={5} fill={fill} stroke="var(--surface)" strokeWidth={2}><title>{`${new Date(r.takenOn).toLocaleDateString('ru-RU')}: ${r.value} ${r.unit || ''}`}</title></circle>
        <text x={xScale(i)} y={yScale(r.value) - 14} fontSize={20} fontWeight={700} fill={labelColor} textAnchor="middle">{r.value}</text>
        <text x={xScale(i)} y={height - 6} fontSize={13} fill="var(--muted)" textAnchor="middle">{new Date(r.takenOn).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</text>
      </g>;
    })}
  </svg>;
}

interface MedicineDraft {
  name: string; ingredient: string; dosage: string; intakeMethod: string; purpose: string;
  dosingScheduleText: string; dosingScheduleSource: string; dosingScheduleSourceUrl: string;
  owner: Owner; amount: string; threshold: string; unit: string; expiry: string; storage: string; photo: string; expiryPhoto: string; usage: string[];
}
const emptyDraft: MedicineDraft = {
  name: '', ingredient: '', dosage: '', intakeMethod: '', purpose: '',
  dosingScheduleText: '', dosingScheduleSource: '', dosingScheduleSourceUrl: '',
  owner: 'shared', amount: '', threshold: '', unit: 'шт.', expiry: '', storage: '', photo: '', expiryPhoto: '', usage: [],
};
type SuggestStatus = {
  state: 'idle' | 'loading' | 'done' | 'error';
  note?: string;
  completedBatches?: number;
  totalBatches?: number;
};
const usagePresets = ['Боль', 'Температура', 'Аллергия', 'ЖКТ', 'Простуда', 'Суставы', 'Витамины'];
// Word stems (not the full preset label) so this matches Russian case endings
// in free text, e.g. "аллергического" for the "Аллергия" preset.
const USAGE_PRESET_STEMS: Record<string, string> = {
  'Боль': 'бол', 'Температура': 'температур', 'Аллергия': 'аллерг', 'ЖКТ': 'жкт',
  'Простуда': 'простуд', 'Суставы': 'сустав', 'Витамины': 'витамин',
};

// Single source of truth for "does this medicine belong to usage preset X" —
// used by both the filter chips and the card's short "для чего" tag, so the
// two always agree: a card can never show a tag its own filter chip
// wouldn't actually find it under.
function medicineText(m: Medicine): string {
  return [m.name, m.activeIngredient, m.dosage, m.category, m.usageSummary, ...(m.usageTags || []), m.note, m.storage]
    .filter(Boolean).join(' ').toLowerCase();
}
function matchesUsagePreset(text: string, preset: string): boolean {
  return text.includes(USAGE_PRESET_STEMS[preset] || preset.toLowerCase());
}

// Duplicate-medicine detection for the add-medicine form: full match on
// name+expiry+dosage+activeIngredient+form (normalized, missing fields
// treated as equal) — deliberately excludes owner (the same physical
// product can be logged under different family members) and any free-text
// field (usageSummary/note/storage/photo naturally vary between two photos
// of the same box). Two medicines matching this key are the same physical
// restock, not just similarly-named products.
function medicineMatchKey(m: Pick<Medicine, 'name' | 'expiry' | 'dosage' | 'activeIngredient' | 'form'>): string {
  const norm = (v?: string) => (v || '').trim().toLowerCase();
  return [norm(m.name), norm(m.expiry), norm(m.dosage), norm(m.activeIngredient), norm(m.form)].join('|');
}

// Short "применение" label for the card header. Prefers the tags the user
// explicitly picked when adding the medicine (usageTags); falls back to
// matching free text against the same usage-preset taxonomy as the filter
// chips for medicines saved before that field existed.
function shortPurpose(m: Medicine): string {
  if (m.usageTags && m.usageTags.length) return m.usageTags.join(', ');
  const text = medicineText(m);
  const matches = usagePresets.filter(preset => matchesUsagePreset(text, preset));
  return matches.length ? matches.join(', ') : m.category || '';
}

const DESCRIPTION_PREVIEW_LENGTH = 100;

function MedicineCard({ m, busy, onRemove, onEdit, overlaps }: { m: Medicine; busy: boolean; onRemove: (id: string) => Promise<void>; onEdit: (m: Medicine) => void; overlaps: Medicine[] }) {
  const [expanded, setExpanded] = useState(false);
  const d = daysUntilExpiry(m.expiry);
  const hasThreshold = m.threshold !== undefined && m.threshold !== '' && Number.isFinite(Number(m.threshold));
  const isLowStock = hasThreshold && Number.isFinite(Number(m.amount)) && Number(m.amount) <= Number(m.threshold);
  const purpose = shortPurpose(m);
  const description = m.usageSummary || '';
  const isLong = description.length > DESCRIPTION_PREVIEW_LENGTH;
  return <article className="medicine-card">
    <div className="medicine-photo">{m.photo ? <img src={m.photo} alt="" /> : <span>{m.name.slice(0, 1).toUpperCase()}</span>}</div>
    <div className="medicine-body">
      <div className="row"><h2>{m.name}</h2><div className="card-actions"><button className="ghost" disabled={busy} onClick={() => onEdit(m)} aria-label="Редактировать">✎</button><button className="ghost danger-text" disabled={busy} onClick={() => confirm(`Удалить ${m.name}?`) && void onRemove(m.id)}>×</button></div></div>
      {m.activeIngredient && <small>
        {m.activeIngredient} {m.dosage}
        {overlaps.length > 0 && <span className="badge danger" title={`Также содержится в: ${overlaps.map(o => o.name).join(', ')}`}>⚠ Также в «{overlaps[0].name}»{overlaps.length > 1 ? ` и ещё ${overlaps.length - 1}` : ''}</span>}
      </small>}
      {m.intakeMethod && <p><b>Как принимать:</b> {m.intakeMethod}</p>}
      {m.dosingScheduleText && <p><b>Схема приёма:</b> {m.dosingScheduleText}{m.dosingScheduleSourceUrl && <> (<a href={m.dosingScheduleSourceUrl} target="_blank" rel="noopener noreferrer">{m.dosingScheduleSource || 'источник'}</a>)</>}</p>}
      <p><b>Применение:</b> {purpose || 'не указано'}</p>
      {hasThreshold && <p><b>Минимальный остаток:</b> {m.threshold} {m.unit || 'шт.'}{isLowStock && <span className="badge warn">Нужно пополнить</span>}</p>}
      {description && <p className="medicine-description">
        <b>Описание:</b> {expanded || !isLong ? description : `${description.slice(0, DESCRIPTION_PREVIEW_LENGTH)}…`}
        {isLong && <button type="button" className="ghost description-toggle" onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
          {expanded ? '▲' : '▼'}
        </button>}
      </p>}
      <footer><span>{m.amount ?? '—'} {m.unit || 'шт.'}</span><span className={d !== null && d < 0 ? 'badge danger' : d !== null && d <= 30 ? 'badge warn' : 'badge'}>{m.expiry ? new Date(m.expiry).toLocaleDateString('ru-RU') : 'срок не указан'}</span></footer>
    </div>
  </article>;
}

// Mobile Safari can discard a backgrounded tab and reload it from scratch —
// that wipes all in-memory React state, silently closing the "add medicine"
// modal mid-edit. sessionStorage survives that kind of reload (it's the same
// tab/browsing session, just re-rendered), so autosave the draft there while
// the modal is open and restore it on mount instead of losing the input.
// Tesseract's own confidence score (0-100) for a recognized line. Below this,
// treat a "found" nameGuess the same as no guess at all — a stylized logo or
// a script tesseract wasn't loaded for (Hebrew, Turkish, etc.) can produce a
// letter-dense but meaningless string with high letter density and low
// confidence, e.g. "Дибикор" read as "Pa НЫ PEEL gv aa".
const OCR_MIN_CONFIDENCE = 60;

const MEDICINE_DRAFT_KEY = 'medai.medicineDraft.v1';
const isDraftBlank = (d: MedicineDraft) =>
  !d.name && !d.photo && !d.ingredient && !d.dosage && !d.intakeMethod && !d.purpose && !d.dosingScheduleText && !d.amount && !d.expiry && !d.storage && !d.usage.length;

function loadSavedMedicineDraft(): MedicineDraft | null {
  try {
    const raw = sessionStorage.getItem(MEDICINE_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? { ...emptyDraft, ...parsed } : null;
  } catch {
    return null;
  }
}

function Pharmacy({ medicines, members, onSave, onUpdate, onRemove, busy, connected }: { medicines: Medicine[]; members: FamilyMember[]; onSave: (m: Medicine) => Promise<void>; onUpdate: (m: Medicine) => Promise<void>; onRemove: (id: string) => Promise<void>; busy: boolean; connected: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [owner, setOwner] = useState<'all' | Owner>('all');
  const [usage, setUsage] = useState('all');
  const restoredDraft = useMemo(loadSavedMedicineDraft, []);
  const [open, setOpen] = useState(() => Boolean(restoredDraft));
  const [draft, setDraft] = useState<MedicineDraft>(() => restoredDraft || emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [ocr, setOcr] = useState<SuggestStatus>({ state: 'idle' });
  const [ai, setAi] = useState<SuggestStatus>({ state: 'idle' });
  const [vision, setVision] = useState<SuggestStatus>({ state: 'idle' });
  const [dosing, setDosing] = useState<SuggestStatus>({ state: 'idle' });
  // The expiry-date flap is a separate recognition aid, not a saved field —
  // only the extracted `expiry` date is persisted (Medicine has no second
  // photo column; see docs/PROJECT_MEMORY.md). It stays local draft state.
  const [expiryOcr, setExpiryOcr] = useState<SuggestStatus>({ state: 'idle' });
  const [expiryVision, setExpiryVision] = useState<SuggestStatus>({ state: 'idle' });
  const [saveError, setSaveError] = useState('');
  const [restoreNotice, setRestoreNotice] = useState(() => Boolean(restoredDraft));
  const [customUsage, setCustomUsage] = useState('');
  const requestedFilter = searchParams.get('filter');
  const attentionFilter = PHARMACY_ATTENTION_FILTERS.some(filter => filter.value === requestedFilter)
    ? requestedFilter as PharmacyAttentionFilter
    : 'all';
  const setAttentionFilter = (filter: PharmacyAttentionFilter) => setSearchParams(filter === 'all' ? {} : { filter });
  // Tracks whether the current draft.name came from the person typing it
  // (or from opening an already-saved record) vs. an OCR/AI auto-fill —
  // only a genuinely user-confirmed name is protected from being replaced
  // by a later, more reliable recognition pass (AI_RULES.md: never
  // auto-overwrite confirmed data, but an auto-filled guess isn't confirmed).
  const [nameIsUserEdited, setNameIsUserEdited] = useState(() => Boolean(restoredDraft?.name));

  const addUsageTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    setDraft(current => current.usage.some(u => u.toLowerCase() === tag.toLowerCase()) ? current : { ...current, usage: [...current.usage, tag] });
  };
  const removeUsageTag = (tag: string) => setDraft(current => ({ ...current, usage: current.usage.filter(u => u !== tag) }));

  useEffect(() => {
    // Don't autosave/restore while editing an existing medicine — the
    // restore flow (loadSavedMedicineDraft) always creates a *new* record,
    // which would silently duplicate the one being edited after a reload.
    if (!open || editingId || isDraftBlank(draft)) return;
    sessionStorage.setItem(MEDICINE_DRAFT_KEY, JSON.stringify(draft));
  }, [draft, open, editingId]);

  const filtered = useMemo(() => medicines.filter(m => {
    const text = medicineText(m);
    const isLowStock = m.threshold !== undefined && m.threshold !== '' && Number.isFinite(Number(m.amount)) && Number.isFinite(Number(m.threshold)) && Number(m.amount) <= Number(m.threshold);
    const matchesAttention = attentionFilter === 'all'
      || (attentionFilter === 'expired' && expiryStatus(m.expiry) === 'expired')
      || (attentionFilter === 'expiring' && expiryStatus(m.expiry) === 'expiring')
      || (attentionFilter === 'missing-expiry' && expiryStatus(m.expiry) === 'missing')
      || (attentionFilter === 'low-stock' && isLowStock);
    return (!query || text.includes(query.toLowerCase()))
      && (owner === 'all' || (m.owner || 'shared') === owner)
      && (usage === 'all' || matchesUsagePreset(text, usage))
      && matchesAttention;
  }), [medicines, query, owner, usage, attentionFilter]);

  // Suggestion only, per docs/AI_RULES.md: an exact active-ingredient text
  // match, not a real drug-interaction database — flags "these two products
  // both list the same substance", not "these two substances interact".
  // Runs against the full medicines list (not `filtered`) so a warning never
  // disappears just because the other product is currently filtered out of
  // view. Two medicines only count as a conflict if they're actually usable
  // by the same person: same owner, or either one is 'shared'.
  const ingredientOverlaps = useMemo(() => {
    const groups = new Map<string, Medicine[]>();
    for (const m of medicines) {
      const key = (m.activeIngredient || '').trim().toLowerCase();
      if (!key) continue;
      (groups.get(key) || groups.set(key, []).get(key)!).push(m);
    }
    const map = new Map<string, Medicine[]>();
    for (const group of groups.values()) {
      if (group.length < 2) continue;
      for (const m of group) {
        const others = group.filter(other => other.id !== m.id && (
          (m.owner || 'shared') === (other.owner || 'shared')
          || (m.owner || 'shared') === 'shared'
          || (other.owner || 'shared') === 'shared'
        ));
        if (others.length) map.set(m.id, others);
      }
    }
    return map;
  }, [medicines]);

  const closeForm = () => { sessionStorage.removeItem(MEDICINE_DRAFT_KEY); setRestoreNotice(false); setCustomUsage(''); setEditingId(null); setOpen(false); };
  const openForm = () => { sessionStorage.removeItem(MEDICINE_DRAFT_KEY); setRestoreNotice(false); setEditingId(null); setDraft(emptyDraft); setNameIsUserEdited(false); setOcr({ state: 'idle' }); setAi({ state: 'idle' }); setVision({ state: 'idle' }); setDosing({ state: 'idle' }); setExpiryOcr({ state: 'idle' }); setExpiryVision({ state: 'idle' }); setSaveError(''); setCustomUsage(''); setOpen(true); };
  const openEditForm = (m: Medicine) => {
    sessionStorage.removeItem(MEDICINE_DRAFT_KEY);
    setRestoreNotice(false);
    setEditingId(m.id);
    setDraft({
      name: m.name, ingredient: m.activeIngredient || '', dosage: m.dosage || '', intakeMethod: m.intakeMethod || '', purpose: m.usageSummary || '',
      dosingScheduleText: m.dosingScheduleText || '', dosingScheduleSource: m.dosingScheduleSource || '', dosingScheduleSourceUrl: m.dosingScheduleSourceUrl || '',
      owner: m.owner || 'shared', amount: m.amount === undefined || m.amount === '' ? '' : String(m.amount),
      threshold: m.threshold === undefined || m.threshold === '' ? '' : String(m.threshold),
      unit: m.unit || 'шт.', expiry: m.expiry || '', storage: m.storage || '', photo: m.photo || '', expiryPhoto: '', usage: m.usageTags || [],
    });
    setNameIsUserEdited(true); // protect the already-saved name from AI auto-overwrite
    setOcr({ state: 'idle' });
    setAi({ state: 'idle' });
    setVision({ state: 'idle' });
    setDosing({ state: 'idle' });
    setExpiryOcr({ state: 'idle' });
    setExpiryVision({ state: 'idle' });
    setSaveError('');
    setCustomUsage('');
    setOpen(true);
  };

  const handlePhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const thumbnail = await compressImage(file);
    setDraft(current => ({ ...current, photo: thumbnail }));

    if (connected) {
      // On-device OCR (tesseract, rus+eng only) is a plain text scanner with
      // no semantic understanding — in practice it essentially never
      // produces a usable result (stylized fonts, non-Latin/non-Cyrillic
      // scripts, rotated photos all defeat it). Go straight to the
      // Claude-vision recognizer instead of wasting a local pass that's
      // very unlikely to help; this off-device photo analysis is disclosed
      // in docs/PRIVACY.md.
      void runVisionAnalysis(thumbnail);
      return;
    }

    // No cloud connection — on-device OCR is the only recognition available.
    setOcr({ state: 'loading' });
    try {
      const result = await recognizeMedicinePhoto(file);
      const nameReliable = Boolean(result.nameGuess) && result.confidence >= OCR_MIN_CONFIDENCE;
      setDraft(current => ({
        ...current,
        name: !nameIsUserEdited && nameReliable ? (result.nameGuess as string) : current.name,
        expiry: current.expiry || result.expiryIso || current.expiry,
      }));
      if (!nameReliable && !result.expiryIso) {
        setOcr({
          state: 'error',
          note: result.expiryHint
            ? `Название не распознано; на фото похоже есть дата ${result.expiryHint} — заполните вручную.`
            : 'Не удалось разобрать текст на фото — заполните поля вручную.',
        });
      } else {
        setOcr({
          state: 'done',
          note: !result.expiryIso && result.expiryHint
            ? `На фото похоже есть дата ${result.expiryHint} — уточните точный срок годности вручную.`
            : 'Название и срок годности подставлены по фото — проверьте перед сохранением.',
        });
      }
    } catch {
      setOcr({ state: 'error', note: 'Не удалось распознать текст на фото — заполните поля вручную.' });
    }
  };

  const runVisionAnalysis = async (photoDataUrl: string, options?: { force?: boolean; expiryOnly?: boolean; setStatus?: (status: SuggestStatus) => void }) => {
    const force = options?.force ?? false;
    // The second "expiry flap" photo exists purely to find a date this
    // package's front photo didn't show — it must never also overwrite the
    // name/dosage/quantity already filled from the main photo.
    const expiryOnly = options?.expiryOnly ?? false;
    const setStatus = options?.setStatus ?? setVision;
    setStatus({ state: 'loading' });
    try {
      const result = await withWakeLock(() => analyzeMedicinePhoto(photoDataUrl, getAnthropicModel()));
      let shouldDescribe = false;
      // Claude vision is the more reliable pass — it should win over a name
      // that on-device OCR merely guessed at, since an OCR guess was never
      // confirmed by the user. It must still never silently touch a name the
      // person actually typed or that came from an already-saved record —
      // that's what nameIsUserEdited distinguishes. That protection is only
      // for the *automatic* OCR-failed fallback though: when the person
      // explicitly presses "Распознать по фото (ИИ)", pressing the button
      // IS their confirmation to replace whatever's there, so `force` skips
      // the guard (the result is still just a draft — nothing saves until
      // they hit "Сохранить").
      const willSetName = !expiryOnly && (force || !nameIsUserEdited) && Boolean(result.nameGuess);
      setDraft(current => {
        shouldDescribe = willSetName && connected;
        return {
          ...current,
          name: willSetName ? (result.nameGuess as string) : current.name,
          expiry: force ? (result.expiryIso || current.expiry) : (current.expiry || result.expiryIso || current.expiry),
          // Printed strength/weight is read straight off this specific
          // package — a name like "Дибикор" or "Терафлю" ships in several
          // different mg/g variants, so this must reflect what's on THIS
          // box, not a generic lookup by name.
          dosage: expiryOnly ? current.dosage : (force ? (result.dosage || current.dosage) : (current.dosage || result.dosage || current.dosage)),
          amount: expiryOnly ? current.amount : (current.amount || (result.quantityEstimate ? String(result.quantityEstimate) : current.amount)),
        };
      });
      // The photo only carries name/expiry/dosage/quantity — chain into the
      // name-based description lookup too, so a photo that local OCR
      // couldn't read still ends up filling active ingredient/usage tags/
      // purpose automatically instead of requiring a second manual click.
      // Passing the just-read dosage gives the model a consistency check
      // against look-alike brand names (e.g. "Brexin"/piroxicam vs.
      // "Bexarotene"/Targretin — an unrelated oncology drug).
      if (shouldDescribe && result.nameGuess) void runAiDescribe(result.nameGuess, result.dosage);
      const parts: string[] = [];
      if (!expiryOnly && result.nameGuess) parts.push(`название «${result.nameGuess}»`);
      if (result.expiryIso) parts.push(`срок годности ${new Date(result.expiryIso).toLocaleDateString('ru-RU')}`);
      else if (result.expiryHint) parts.push(`на фото похоже есть дата «${result.expiryHint}» — уточните вручную`);
      if (!expiryOnly && result.dosage) parts.push(`дозировка ${result.dosage} — по надписи на упаковке`);
      if (!expiryOnly && result.quantityEstimate) {
        parts.push(result.quantityBasis === 'label'
          ? `количество ${result.quantityEstimate} — по надписи на упаковке`
          : `количество ≈ ${result.quantityEstimate} — оценка по фото`);
      }
      if (result.note) parts.push(result.note);
      setStatus({
        state: 'done',
        note: `${parts.length ? parts.join('; ') : expiryOnly ? 'Срок годности не удалось определить и на этом фото' : 'Не удалось определить название, срок, дозировку или количество по фото'} — обязательно проверьте перед сохранением.`,
      });
    } catch (error) {
      setStatus({ state: 'error', note: error instanceof Error ? error.message : 'Не удалось проанализировать фото.' });
    }
  };

  const handleAnalyzePhoto = async () => {
    if (!draft.photo || !connected) return;
    await runVisionAnalysis(draft.photo, { force: true });
  };

  const handleExpiryPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const thumbnail = await compressImage(file);
    setDraft(current => ({ ...current, expiryPhoto: thumbnail }));

    if (connected) {
      void runVisionAnalysis(thumbnail, { expiryOnly: true, setStatus: setExpiryVision });
      return;
    }

    setExpiryOcr({ state: 'loading' });
    try {
      const result = await recognizeMedicinePhoto(file);
      setDraft(current => ({ ...current, expiry: current.expiry || result.expiryIso || current.expiry }));
      setExpiryOcr(result.expiryIso
        ? { state: 'done', note: 'Срок годности распознан — проверьте перед сохранением.' }
        : result.expiryHint
        ? { state: 'done', note: `На фото похоже есть дата «${result.expiryHint}» — уточните точный срок годности вручную.` }
        : { state: 'error', note: 'Не удалось разобрать дату на фото — заполните срок годности вручную.' });
    } catch {
      setExpiryOcr({ state: 'error', note: 'Не удалось распознать дату на фото.' });
    }
  };

  const runAiDescribe = async (name: string, dosage?: string) => {
    setAi({ state: 'loading' });
    try {
      const result = await withWakeLock(() => describeMedicine(name, getAnthropicModel(), dosage));
      setDraft(current => ({
        ...current,
        ingredient: current.ingredient || result.activeIngredient || current.ingredient,
        dosage: current.dosage || result.dosage || current.dosage,
        intakeMethod: current.intakeMethod || result.intakeMethod || current.intakeMethod,
        usage: current.usage.length ? current.usage : (result.usageTags?.length ? result.usageTags : current.usage),
        purpose: current.purpose || result.usageSummary || current.purpose,
      }));
      setAi({ state: 'done', note: 'Заполнено ИИ (Claude) — проверьте перед сохранением.' });
    } catch (error) {
      setAi({ state: 'error', note: error instanceof Error ? error.message : 'Не удалось получить описание — заполните вручную.' });
    }
  };

  const handleAiDescribe = async () => {
    if (!draft.name.trim() || !connected) return;
    await runAiDescribe(draft.name.trim(), draft.dosage.trim() || undefined);
  };

  const handleFindDosing = async () => {
    if (!draft.name.trim() || !connected) return;
    setDosing({ state: 'loading' });
    try {
      const result = await withWakeLock(() => findMedicineDosing(draft.name.trim(), draft.ingredient.trim(), draft.dosage.trim(), getAnthropicModel()));
      if (result.found) {
        setDraft(current => ({
          ...current,
          dosingScheduleText: result.dosingScheduleText,
          dosingScheduleSource: result.sourceName || '',
          dosingScheduleSourceUrl: result.sourceUrl,
        }));
        setDosing({ state: 'done', note: 'Найдено на официальной странице — проверьте перед сохранением.' });
      } else {
        setDosing({ state: 'error', note: 'Не нашли официальную инструкцию с явно указанной схемой приёма. Уточните по инструкции к своей упаковке или у врача.' });
      }
    } catch (error) {
      setDosing({ state: 'error', note: error instanceof Error ? error.message : 'Не удалось выполнить поиск.' });
    }
  };

  const clearDosing = () => {
    setDraft(current => ({ ...current, dosingScheduleText: '', dosingScheduleSource: '', dosingScheduleSourceUrl: '' }));
    setDosing({ state: 'idle' });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft.name.trim()) return;
    setSaveError('');
    try {
      const payload: Medicine = {
        id: editingId || makeId('med'), name: draft.name.trim(), activeIngredient: draft.ingredient.trim(),
        dosage: draft.dosage.trim(), intakeMethod: draft.intakeMethod.trim() || undefined, usageSummary: draft.purpose.trim(),
        dosingScheduleText: draft.dosingScheduleText.trim() || undefined,
        dosingScheduleSource: draft.dosingScheduleSource.trim() || undefined,
        dosingScheduleSourceUrl: draft.dosingScheduleText.trim() ? (draft.dosingScheduleSourceUrl.trim() || undefined) : undefined,
        usageTags: draft.usage, owner: draft.owner, amount: Number(draft.amount || 0), threshold: draft.threshold === '' ? undefined : Number(draft.threshold), unit: draft.unit.trim() || 'шт.',
        expiry: draft.expiry, storage: draft.storage.trim(), photo: draft.photo,
      };
      if (!editingId) {
        const key = medicineMatchKey(payload);
        const match = medicines.find(existing => medicineMatchKey(existing) === key);
        if (match) {
          const newTotal = Number(match.amount || 0) + Number(payload.amount || 0);
          const merge = confirm(
            `Похоже, «${match.name}» с таким же сроком годности и дозировкой уже есть в аптечке ` +
            `(сейчас ${match.amount ?? 0} ${match.unit || 'шт.'}).\n\n` +
            `Объединить количество (станет ${newTotal} ${match.unit || 'шт.'})?\n` +
            `Отмена — сохранить как отдельную запись.`
          );
          if (merge) {
            await onUpdate({ ...match, amount: newTotal });
            sessionStorage.removeItem(MEDICINE_DRAFT_KEY);
            setEditingId(null);
            setOpen(false);
            return;
          }
        }
      }
      if (editingId) await onUpdate(payload);
      else await onSave(payload);
      sessionStorage.removeItem(MEDICINE_DRAFT_KEY);
      setEditingId(null);
      setOpen(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Не удалось сохранить лекарство');
    }
  };

  return <div className="stack">
    <section className="page-head row"><div><p className="eyebrow">Домашняя аптечка</p><h1>Лекарства</h1><p>Поиск работает по названию, веществу и назначению.</p></div><div className="page-head-actions"><button className="primary round" onClick={openForm}>＋</button></div></section>
    <input className="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Например: аллергия или ибупрофен" />
    <div className="filter-bar">
      <select aria-label="Состояние запасов" value={attentionFilter} onChange={e => setAttentionFilter(e.target.value as PharmacyAttentionFilter)}>
        {PHARMACY_ATTENTION_FILTERS.map(filter => <option key={filter.value} value={filter.value}>{filter.label}</option>)}
      </select>
      <select aria-label="Применение" value={usage} onChange={e => setUsage(e.target.value)}>
        {['all', ...usagePresets].map(x => <option key={x} value={x}>{x === 'all' ? 'Все применения' : x}</option>)}
      </select>
      <select aria-label="Владелец" value={owner} onChange={e => setOwner(e.target.value)}>
        {(['all', 'shared', ...members.map(member => member.userId)] as const).map(x => <option key={x} value={x}>{x === 'all' ? 'Все' : x === 'shared' ? 'Общее' : profileName(x, members)}</option>)}
      </select>
    </div>
    <section className="medicine-grid">{filtered.map(m => <MedicineCard key={m.id} m={m} busy={busy} onRemove={onRemove} onEdit={openEditForm} overlaps={ingredientOverlaps.get(m.id) || []} />)}</section>
    {!filtered.length && <section className="panel empty"><h2>Ничего не найдено</h2><p>Измени запрос или добавь первое лекарство.</p></section>}
    {open && <div className="modal" role="dialog" aria-modal="true"><form className="form" onSubmit={submit}>
      <div className="row"><h2>{editingId ? 'Изменить лекарство' : 'Новое лекарство'}</h2><button type="button" className="ghost" onClick={closeForm}>×</button></div>
      {restoreNotice && <p className="ai-hint">Черновик восстановлен после перезагрузки страницы — проверьте поля перед сохранением.</p>}
      <label className="photo-field">
        <span>Фото упаковки</span>
        <div className="photo-picker">
          {draft.photo ? <img src={draft.photo} alt="" className="photo-preview" /> : <span className="photo-placeholder" aria-hidden="true">📷</span>}
          <span className="file-picker-button">
            {draft.photo ? 'Изменить фото' : 'Выбрать фото'}
            <input type="file" accept="image/*" onChange={handlePhoto} />
          </span>
        </div>
        {ocr.state === 'loading' && <small className="hint-status">Распознаём текст с фото…</small>}
        {ocr.state === 'error' && <small className="hint-status hint-status--error">{ocr.note}</small>}
        {ocr.state === 'done' && <small className="hint-status hint-status--ok">{ocr.note}</small>}
      </label>
      {draft.photo && connected && <div className="ai-describe">
        <button type="button" className="secondary" disabled={vision.state === 'loading'} onClick={() => void handleAnalyzePhoto()}>{vision.state === 'loading' ? 'Смотрим фото…' : '🔍 Распознать по фото (ИИ)'}</button>
        {vision.state === 'error' && <small className="hint-status hint-status--error">{vision.note}</small>}
        {vision.state === 'done' && <small className="hint-status hint-status--ok">{vision.note}</small>}
      </div>}
      <label className="photo-field">
        <span>Фото срока годности (если не видно на основном фото)</span>
        <div className="photo-picker">
          {draft.expiryPhoto ? <img src={draft.expiryPhoto} alt="" className="photo-preview" /> : <span className="photo-placeholder" aria-hidden="true">📅</span>}
          <span className="file-picker-button">
            {draft.expiryPhoto ? 'Переснять' : 'Добавить фото'}
            <input type="file" accept="image/*" onChange={handleExpiryPhoto} />
          </span>
        </div>
        <small className="hint-status">Отдельное служебное фото только для распознавания даты — само не сохраняется, в карточке лекарства не показывается.</small>
        {expiryOcr.state === 'loading' && <small className="hint-status">Распознаём дату…</small>}
        {expiryOcr.state === 'error' && <small className="hint-status hint-status--error">{expiryOcr.note}</small>}
        {expiryOcr.state === 'done' && <small className="hint-status hint-status--ok">{expiryOcr.note}</small>}
        {expiryVision.state === 'loading' && <small className="hint-status">Ищем дату на фото…</small>}
        {expiryVision.state === 'error' && <small className="hint-status hint-status--error">{expiryVision.note}</small>}
        {expiryVision.state === 'done' && <small className="hint-status hint-status--ok">{expiryVision.note}</small>}
      </label>
      <label>Название<input value={draft.name} onChange={e => { setDraft(d => ({ ...d, name: e.target.value })); setNameIsUserEdited(true); }} required /></label>
      <div className="two"><label>Действующее вещество<input value={draft.ingredient} onChange={e => setDraft(d => ({ ...d, ingredient: e.target.value }))} /></label><label>Дозировка<input value={draft.dosage} onChange={e => setDraft(d => ({ ...d, dosage: e.target.value }))} /></label></div>
      <label>Как принимать<input value={draft.intakeMethod} onChange={e => setDraft(d => ({ ...d, intakeMethod: e.target.value }))} placeholder="Например: глотать целиком, запивая водой" /><small className="hint-status">Способ приёма (глотать/разжевать/рассосать/растворить) — без кратности и суточной дозы; это не назначение врача, уточняйте по инструкции.</small></label>
      {connected
        ? <div className="ai-describe"><button type="button" className="secondary" disabled={!draft.name.trim() || ai.state === 'loading'} onClick={() => void handleAiDescribe()}>{ai.state === 'loading' ? 'Спрашиваем ИИ…' : '✨ Описание по ИИ (Claude)'}</button>{ai.state === 'error' && <small className="hint-status hint-status--error">{ai.note}</small>}{ai.state === 'done' && <small className="hint-status hint-status--ok">{ai.note}</small>}</div>
        : <p className="ai-hint">Описание по ИИ доступно после входа в облачную аптечку (см. «Ещё»).</p>}
      {connected && <div className="ai-describe">
        <button type="button" className="secondary" disabled={!draft.name.trim() || dosing.state === 'loading'} onClick={() => void handleFindDosing()}>{dosing.state === 'loading' ? 'Ищем официальную инструкцию…' : '🔎 Найти схему приёма (поиск в сети)'}</button>
        <small className="hint-status">Ищет только на официальных страницах (производитель, реестр лекарств, справочники) и переносит дозу/кратность, только если там написано явно — без догадок по памяти.</small>
        {dosing.state === 'error' && <small className="hint-status hint-status--error">{dosing.note}</small>}
        {dosing.state === 'done' && <small className="hint-status hint-status--ok">{dosing.note}</small>}
      </div>}
      {draft.dosingScheduleText && <div className="stack" style={{ gap: 6 }}>
        <label>Схема приёма (из найденного источника)<textarea value={draft.dosingScheduleText} onChange={e => setDraft(d => ({ ...d, dosingScheduleText: e.target.value }))} rows={3} /></label>
        {draft.dosingScheduleSourceUrl && <p className="ai-hint">Источник: <a href={draft.dosingScheduleSourceUrl} target="_blank" rel="noopener noreferrer">{draft.dosingScheduleSource || draft.dosingScheduleSourceUrl}</a>. Цитата с найденной страницы, не рекомендация приложения — дозировка может отличаться для вашей упаковки/страны, сверяйтесь с инструкцией или врачом.</p>}
        <button type="button" className="secondary danger-button" onClick={clearDosing}>✕ Убрать схему приёма</button>
      </div>}
      <label>Владелец<select value={draft.owner} onChange={e => setDraft(d => ({ ...d, owner: e.target.value as Owner }))}><option value="shared">Общее</option>{members.map(member => <option key={member.userId} value={member.userId}>{member.displayName}</option>)}</select></label>
      <label>Применение
        <div className="usage-picker">
          <select value="" onChange={e => { addUsageTag(e.target.value); e.target.value = ''; }}>
            <option value="" disabled>Выбрать из списка…</option>
            {usagePresets.filter(p => !draft.usage.some(u => u.toLowerCase() === p.toLowerCase())).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="usage-custom">
            <input value={customUsage} onChange={e => setCustomUsage(e.target.value)} placeholder="Своё применение…"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addUsageTag(customUsage); setCustomUsage(''); } }} />
            <button type="button" className="secondary" onClick={() => { addUsageTag(customUsage); setCustomUsage(''); }}>Добавить</button>
          </div>
        </div>
        {draft.usage.length > 0 && <div className="chips">{draft.usage.map(tag => (
          <button key={tag} type="button" className="active" onClick={() => removeUsageTag(tag)}>{tag} ×</button>
        ))}</div>}
      </label>
      <label>Для чего<textarea value={draft.purpose} onChange={e => setDraft(d => ({ ...d, purpose: e.target.value }))} placeholder="Например: от боли и температуры" /></label>
      <div className="two"><label>Количество<input type="number" min="0" step="any" value={draft.amount} onChange={e => setDraft(d => ({ ...d, amount: e.target.value }))} /></label><label>Минимальный остаток<input type="number" min="0" step="any" value={draft.threshold} onChange={e => setDraft(d => ({ ...d, threshold: e.target.value }))} /><small className="hint-status">Необязательно: появится напоминание о пополнении.</small></label></div>
      <label>Единица<input value={draft.unit} onChange={e => setDraft(d => ({ ...d, unit: e.target.value }))} /></label>
      <div className="two"><label>Срок годности<input type="date" value={draft.expiry} onChange={e => setDraft(d => ({ ...d, expiry: e.target.value }))} /></label><label>Хранение<input value={draft.storage} onChange={e => setDraft(d => ({ ...d, storage: e.target.value }))} /></label></div>
      {saveError && <p className="form-error" role="alert">{saveError}</p>}
      <button className="primary" type="submit" disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
    </form></div>}
  </div>;
}

function More({ configured, connected, hasHousehold, localMode, householdId, householdName, householdRole, members, documents, email, onClaimLegacyProfile, onDeleteResultsForOwner, onDeleteResultsForDocument, theme, onTheme, onSignOut, onExitLocalMode }: { configured: boolean; connected: boolean; hasHousehold: boolean; localMode: boolean; householdId?: string; householdName: string; householdRole: 'owner' | 'member' | null; members: FamilyMember[]; documents: MedicalDocument[]; email: string; onClaimLegacyProfile: (legacyOwner: 'anton' | 'anya', memberUserId: string) => Promise<void>; onDeleteResultsForOwner: (owner: Profile) => Promise<number>; onDeleteResultsForDocument: (documentId: string) => Promise<number>; theme: string; onTheme: (t: 'auto'|'light'|'dark') => void; onSignOut: () => Promise<void>; onExitLocalMode: () => void }) {
  useEffect(() => { document.documentElement.dataset.theme = theme === 'auto' ? '' : theme; }, [theme]);
  const [model, setModel] = useState(getAnthropicModel());
  const [inviteEmail, setInviteEmail] = useState('');
  const [familyMessage, setFamilyMessage] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [renameName, setRenameName] = useState(householdName);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [legacyAntonMember, setLegacyAntonMember] = useState('');
  const [legacyAnyaMember, setLegacyAnyaMember] = useState('');
  const [claimingLegacy, setClaimingLegacy] = useState<'anton' | 'anya' | ''>('');
  const [deleteOwner, setDeleteOwner] = useState<Profile>(() => members[0]?.userId || 'anton');
  const [deleteScope, setDeleteScope] = useState<'profile'|'document'>('profile');
  const [deleteDocumentId, setDeleteDocumentId] = useState('');
  const deletableDocuments = documents.filter(document => document.owner === deleteOwner);
  const deletePhrase = deleteScope === 'profile' ? `УДАЛИТЬ ПОКАЗАТЕЛИ ${profileName(deleteOwner, members).toLocaleUpperCase('ru-RU')}` : 'УДАЛИТЬ ПОКАЗАТЕЛИ ДОКУМЕНТА';
  const [deleteMessage, setDeleteMessage] = useState('');
  useEffect(() => { setRenameName(householdName); }, [householdName]);
  useEffect(() => { if (!members.some(member => member.userId === deleteOwner)) setDeleteOwner(members[0]?.userId || 'anton'); }, [members, deleteOwner]);

  const claimLegacy = async (legacyOwner: 'anton' | 'anya', memberUserId: string) => {
    if (!memberUserId) { setFamilyMessage('Сначала выберите участника семьи.'); return; }
    const member = members.find(item => item.userId === memberUserId);
    if (!member) return;
    const legacyName = legacyOwner === 'anton' ? '«Антон»' : '«Аня»';
    if (!confirm(`Перенести все ещё не перенесённые анализы и документы старого профиля ${legacyName} к участнику «${member.displayName}»? Это действие нельзя отменить автоматически.`)) return;
    setClaimingLegacy(legacyOwner); setFamilyMessage('');
    try {
      await onClaimLegacyProfile(legacyOwner, memberUserId);
    } catch (error) {
      setFamilyMessage(errorText(error));
      setClaimingLegacy('');
    }
  };

  const changeModel = (value: string) => {
    setModel(value);
    setAnthropicModel(value);
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setFamilyMessage('Ссылка скопирована. Отправьте её только человеку с указанным email.');
    } catch {
      setFamilyMessage('Не удалось скопировать автоматически — скопируйте ссылку из поля вручную.');
    }
  };

  return <div className="stack"><section className="page-head"><p className="eyebrow">Приложение</p><h1>Ещё</h1></section><section className="panel setting"><div><h2>Supabase</h2><p>{connected ? `Выполнен вход: ${email}` : configured ? 'Доступен, но открыт локальный режим' : 'Переменные окружения не настроены'}</p></div><span className={connected ? 'badge ok' : 'badge'}>{connected ? 'В облаке' : 'Локально'}</span></section>{connected && <section className="panel"><h2>Семья</h2>{hasHousehold ? <><p>Текущая семья: <b>{householdName}</b>{householdRole === 'owner' ? ' · владелец' : ''}.</p><section className="family-members" aria-labelledby="family-members-heading"><h3 id="family-members-heading">Участники семьи</h3><ul>{members.map(member => <li key={member.userId}><span>{member.displayName}</span><span className={member.role === 'owner' ? 'badge ok' : 'badge'}>{member.role === 'owner' ? 'Владелец' : 'Участник'}</span></li>)}</ul></section>{householdRole === 'owner' && <><div className="two"><input value={renameName} onChange={event => setRenameName(event.target.value)} aria-label="Название семьи" /><button className="secondary" onClick={() => { if (!householdId) return; void renameHousehold(householdId, renameName).then(() => window.location.reload()).catch(error => setFamilyMessage(errorText(error))); }}>Переименовать</button></div><section className="stack legacy-profile-migration"><div><h3>Перенести старые медицинские профили</h3><p>Выберите, кому принадлежат прежние записи «Антон» и «Аня». Переносит только ещё не связанные анализы и документы; действие нужно подтвердить отдельно для каждого профиля.</p></div>{(['anton', 'anya'] as const).map(legacyOwner => { const value = legacyOwner === 'anton' ? legacyAntonMember : legacyAnyaMember; const setValue = legacyOwner === 'anton' ? setLegacyAntonMember : setLegacyAnyaMember; return <div className="two" key={legacyOwner}><label>Старый профиль «{legacyOwner === 'anton' ? 'Антон' : 'Аня'}»<select value={value} onChange={event => setValue(event.target.value)}><option value="">Выберите участника</option>{members.map(member => <option key={member.userId} value={member.userId}>{member.displayName}</option>)}</select></label><button type="button" className="secondary" disabled={!value || Boolean(claimingLegacy)} onClick={() => void claimLegacy(legacyOwner, value)}>{claimingLegacy === legacyOwner ? 'Переносим…' : 'Подтвердить перенос'}</button></div>; })}</section></>}<p>Любой участник может пригласить близкого. Приглашение действует 14 дней и принимается только под указанным email.</p><div className="two"><input type="email" value={inviteEmail} onChange={event => setInviteEmail(event.target.value)} placeholder="email члена семьи" /><button className="secondary" onClick={() => { if (!householdId) return; void createHouseholdInvitation(householdId, inviteEmail).then(link => { setInviteLink(link); setFamilyMessage('Персональная ссылка создана.'); setInviteEmail(''); }).catch(error => setFamilyMessage(errorText(error))); }}>Создать приглашение</button></div>{inviteLink && <div className="stack"><label>Персональная ссылка<input readOnly value={inviteLink} aria-label="Персональная ссылка-приглашение" /></label><button className="secondary" onClick={() => void copyInviteLink()}>Копировать ссылку</button></div>}</> : <><p>У вас пока нет семьи. Создайте новую или откройте личную ссылку-приглашение, отправленную вам близким.</p><div className="two"><input value={familyName} onChange={event => setFamilyName(event.target.value)} placeholder="Название семьи" /><button className="primary" onClick={() => void createPersonalHousehold(familyName).then(() => window.location.reload()).catch(error => setFamilyMessage(errorText(error)))}>Создать семью</button></div></>}{familyMessage && <p className="form-success">{familyMessage}</p>}</section>} {connected && <section className="panel"><h2>Аккаунт</h2><button className="secondary" onClick={() => void onSignOut()}>Выйти</button></section>}
    {configured && localMode && !connected && <section className="panel"><h2>Аккаунт</h2><p>Сейчас данные хранятся только в этом браузере и не видны второму пользователю. Войдите, чтобы подключиться к общей облачной аптечке.</p><button className="secondary" onClick={onExitLocalMode}>Войти в облачную аптечку</button></section>}
    {connected && <section className="panel"><h2>Управление показателями</h2><p>Выберите профиль и область удаления. PDF-документы и лекарства не удаляются.</p><label>Профиль<select value={deleteOwner} onChange={event => { setDeleteOwner(event.target.value as Profile); setDeleteDocumentId(''); }}>{members.map(member => <option key={member.userId} value={member.userId}>{member.displayName}</option>)}</select></label><label>Удалить<select value={deleteScope} onChange={event => { setDeleteScope(event.target.value as 'profile'|'document'); setDeleteConfirmation(''); }}><option value="profile">Все показатели профиля</option><option value="document">Показатели одного документа</option></select></label>{deleteScope === 'document' && <label>Документ<select value={deleteDocumentId} onChange={event => setDeleteDocumentId(event.target.value)}><option value="">Выберите PDF/фото</option>{deletableDocuments.map(document => <option value={document.id} key={document.id}>{document.originalName} · {formatOptionalDocumentDate(document.takenOn)}</option>)}</select></label>}<label>Чтобы подтвердить, введите «{deletePhrase}»<input value={deleteConfirmation} onChange={event => setDeleteConfirmation(event.target.value)} /></label><button className="secondary" disabled={deleteConfirmation !== deletePhrase || (deleteScope === 'document' && !deleteDocumentId)} onClick={() => { const deletion = deleteScope === 'profile' ? onDeleteResultsForOwner(deleteOwner) : onDeleteResultsForDocument(deleteDocumentId); void deletion.then(count => { setDeleteMessage(`Удалено показателей: ${count}.`); setDeleteConfirmation(''); }).catch(error => setDeleteMessage(errorText(error))); }}>{deleteScope === 'profile' ? 'Удалить показатели профиля' : 'Удалить показатели документа'}</button>{deleteMessage && <p className="form-success">{deleteMessage}</p>}</section>}
    <section className="panel">
      <h2>Описание по ИИ (Claude)</h2>
      <p>В форме «Добавить лекарство» кнопка «Описание по ИИ» подсказывает действующее вещество, категорию и назначение по названию. Ключ Anthropic хранится на сервере (в секретах Supabase Edge Function) и общий для всей семьи — в браузере он не сохраняется. Доступно только после входа в облачную аптечку.</p>
      <label>Модель<select value={model} onChange={e => changeModel(e.target.value)}>{ANTHROPIC_MODEL_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
    </section>
    <section className="panel">
      <h2>Рекомендации ИИ — не медицинский совет</h2>
      <p>Рекомендации по показателям и описания лекарств формируются ИИ на основе введённых вами данных и носят исключительно ознакомительный характер. Это не диагноз, не назначение лечения и не медицинский совет. При любых отклонениях в показателях или перед изменением образа жизни (питание, физическая активность и т.п.) обязательно проконсультируйтесь с врачом.</p>
    </section>
    <section className="panel"><h2>Оформление</h2><div className="chips">{(['auto','light','dark'] as const).map(t => <button key={t} className={theme === t ? 'active' : ''} onClick={() => onTheme(t)}>{t === 'auto' ? 'Системное' : t === 'light' ? 'Светлое' : 'Тёмное'}</button>)}</div></section>
    <section className="panel">
      <p className="eyebrow">О приложении</p>
      <h2>MedApp</h2>
      <p><b>Версия:</b> {import.meta.env.VITE_APP_VERSION}</p>
      <p><b>Сборка:</b> {import.meta.env.VITE_GIT_COMMIT}</p>
      <p><b>Дата сборки:</b> {formatOptionalDocumentDateTime(import.meta.env.VITE_BUILD_DATE)}</p>
      <p><b>Платформа:</b> Cloudflare Workers + Supabase</p>
    </section>
  </div>;
}
